import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Radio,
  RadioGroup,
  FormControlLabel,
  TextField,
  Paper,
  Button,
  IconButton,
  Modal,
  Fade,
  Tooltip,
  FormControl,
  FormLabel, LinearProgress, Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  Pagination,
  MenuItem,
  Select,
  FormGroup,
  Checkbox,
  Box as MuiBox,
  DialogContentText,
} from "@mui/material";
// import CheckCircle from "@mui/icons-material/CheckCircle";
import EmojiObjectsIcon from '@mui/icons-material/EmojiObjects';
import { useParams, useLocation, useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import TrophyIcon from "@mui/icons-material/EmojiEvents";
import ErrorIcon from "@mui/icons-material/Error";
import InfoIcon from "@mui/icons-material/Info";
import CorrectIcon from "@mui/icons-material/CheckCircle";
import IncorrectIcon from "@mui/icons-material/Cancel";
import TimeIcon from "@mui/icons-material/AccessTime";
import { useContext } from "react";
import { ThemeContext } from '../context/ThemeContext';
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import sanitizeHtml from "sanitize-html";
import katex from "katex";
import "katex/dist/katex.min.css";

import CheckCircle from "@mui/icons-material/CheckCircle";
import Cancel from "@mui/icons-material/Cancel";
import Chip from "@mui/material/Chip";



const ImageQuestionDisplay = ({ question, userAnswers, onAnswerChange, mode }) => {
  const canvasRef = useRef(null);
  const [baseImageLoaded, setBaseImageLoaded] = useState(false);
  const [draggedAnswer, setDraggedAnswer] = useState(null);

  useEffect(() => {
    if (!canvasRef.current || !question.file_url || !question.labels || baseImageLoaded) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const img = new window.Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      let canvasWidth, canvasHeight;

      if (question.canvas_width && question.canvas_height) {
        canvasWidth = question.canvas_width;
        canvasHeight = question.canvas_height;
      } else {
        const maxWidth = 800;
        const aspectRatio = img.width / img.height;
        canvasWidth = Math.min(maxWidth, img.width);
        canvasHeight = canvasWidth / aspectRatio;
      }

      canvas.width = canvasWidth;
      canvas.height = canvasHeight;
      ctx.drawImage(img, 0, 0, canvasWidth, canvasHeight);

      const colors = [
        'rgba(255, 0, 0, 1)',
        'rgba(0, 0, 255, 1)',
        'rgba(0, 255, 0, 1)',
        'rgba(255, 255, 0, 1)',
        'rgba(255, 0, 255, 1)',
        'rgba(0, 255, 255, 1)',
        'rgba(255, 128, 0, 1)',
        'rgba(128, 0, 255, 1)',
      ];

      question.labels.forEach((label, index) => {
        ctx.fillStyle = colors[index % colors.length];
        ctx.fillRect(label.x, label.y, label.width, label.height);

        ctx.fillStyle = '#ffffff';
        ctx.font = 'bold 24px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(
          label.id,
          label.x + label.width / 2,
          label.y + label.height / 2
        );
      });

      setBaseImageLoaded(true);
    };

    img.onerror = () => {
      console.error('Failed to load image:', question.file_url);
    };

    img.src = question.file_url.startsWith('http')
      ? question.file_url
      : `http://warals1.ddns.net:9017${question.file_url}`;
  }, [question.file_url, question.labels, baseImageLoaded, question.canvas_width, question.canvas_height]);

  return (
    <Box sx={{ textAlign: "center", mt: 2 }}>
      {/* Light Bulb Note */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          mb: 2,
          p: 1.5,
          borderRadius: 2,
          backgroundColor: mode === "dark"
            ? 'rgba(255, 193, 7, 0.15)'
            : 'rgba(255, 193, 7, 0.1)',
          border: `1px solid ${mode === "dark"
            ? 'rgba(255, 193, 7, 0.3)'
            : 'rgba(255, 193, 7, 0.3)'}`,
          boxShadow: mode === "dark"
            ? '0 2px 8px rgba(255, 193, 7, 0.2)'
            : '0 2px 8px rgba(255, 193, 7, 0.15)',
        }}
      >
        <EmojiObjectsIcon
          sx={{
            color: mode === "dark" ? '#ffc107' : '#f57c00',
            fontSize: '1.3rem',
          }}
        />
        <Typography
          variant="body2"
          sx={{
            color: mode === "dark" ? '#fff' : '#5d4037',
            fontWeight: 600,
            fontSize: '0.875rem',
            fontStyle: 'italic',
          }}
        >
          Note: Each label blank in this image question carries a separate mark
        </Typography>
      </Box>

      <canvas
        ref={canvasRef}
        style={{
          maxWidth: "100%",
          border: "1px solid #ccc",
          borderRadius: "8px",
          display: "block",
          margin: "0 auto",
        }}
      />
      <Typography variant="body2" sx={{ mt: 1, color: "#666" }}>
        {question.statement}
      </Typography>

      {question.labels && question.labels.length > 0 && (
        <Box sx={{ mt: 3, px: 2 }}>
          {/* Available Answers Pool */}
          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
            Drag answers to the correct labels:
          </Typography>
          <Box sx={{
            display: 'flex',
            gap: 1,
            flexWrap: 'wrap',
            p: 2,
            bgcolor: mode === "dark" ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
            borderRadius: 2,
            minHeight: 60,
            border: '2px dashed rgba(30, 139, 175, 0.3)'
          }}>
            {question.answers?.map((answer, idx) => {
              const isUsed = Object.values(userAnswers || {}).includes(answer.answer_text);
              if (isUsed) return null;

              return (
                <Chip
                  key={idx}
                  label={answer.answer_text}
                  draggable
                  onDragStart={() => setDraggedAnswer(answer.answer_text)}
                  onDragEnd={() => setDraggedAnswer(null)}
                  sx={{
                    cursor: 'grab',
                    '&:active': { cursor: 'grabbing' },
                    bgcolor: '#1e8bafff',
                    color: 'white',
                    fontWeight: 600,
                    '&:hover': { bgcolor: '#1e3a8a' }
                  }}
                />
              );
            })}
            {question.answers?.every(ans => Object.values(userAnswers || {}).includes(ans.answer_text)) && (
              <Typography variant="caption" color="text.secondary" sx={{ m: 'auto' }}>
                All answers placed
              </Typography>
            )}
          </Box>

          {/* Drop Zones for Labels */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, mt: 2 }}>
            {question.labels.map((label) => {
              const currentAnswer = userAnswers?.[label.id];

              return (
                <Box
                  key={label.id}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (draggedAnswer) {
                      onAnswerChange(question.id, draggedAnswer, false, label.id);
                      setDraggedAnswer(null);
                    }
                  }}
                  sx={{
                    p: 2,
                    border: '2px dashed',
                    borderColor: currentAnswer ? '#4caf50' : 'grey.400',
                    borderRadius: 2,
                    bgcolor: currentAnswer
                      ? mode === "dark" ? 'rgba(76, 175, 80, 0.2)' : 'rgba(76, 175, 80, 0.1)'
                      : mode === "dark" ? 'rgba(255,255,255,0.03)' : 'background.paper',
                    minHeight: 70,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: currentAnswer ? '#4caf50' : '#1e8bafff',
                      bgcolor: currentAnswer
                        ? mode === "dark" ? 'rgba(76, 175, 80, 0.3)' : 'rgba(76, 175, 80, 0.15)'
                        : mode === "dark" ? 'rgba(30, 139, 175, 0.1)' : 'rgba(30, 139, 175, 0.05)'
                    }
                  }}
                >
                  <Typography variant="body2" fontWeight={600}>
                    Label {label.id}:
                  </Typography>
                  {currentAnswer ? (
                    <Chip
                      label={currentAnswer}
                      onDelete={() => onAnswerChange(question.id, '', false, label.id)}
                      color="success"
                      size="small"
                    />
                  ) : (
                    <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      Drop answer here
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
    </Box>
  );
};


const PracticePage = () => {
    const { state } = useLocation();
// Add these after existing state declarations:
const passedQuizId = state?.quizId;
const passedNegMarking = state?.negativeMarking ?? false;
const passedTimerEnabled = state?.timerEnabled ?? false;

  const [loadedImages, setLoadedImages] = useState({});
  const [imageLabels, setImageLabels] = useState({});
  const { folderId } = useParams();
  const imageCanvasRefs = useRef({});
  const imageRefs = useRef({});
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [userAnswers, setUserAnswers] = useState({});
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [tempAnswers, setTempAnswers] = useState({});
  const [isStartModalOpen, setIsStartModalOpen] = useState(false);
  const [shouldShowStartModal, setShouldShowStartModal] = useState(false);
  const [maxAttempts, setMaxAttempts] = useState("");
  const [passingPercentage, setPassingPercentage] = useState("");
  const [negativeMarking, setNegativeMarking] = useState(false);
  const [isTimerEnabled, setIsTimerEnabled] = useState(false);
  const [quizId, setQuizId] = useState(null);
  const [results, setResults] = useState(null);
  const [questionStatuses, setQuestionStatuses] = useState({}); // Track question statuses
  const [questionListOpen, setQuestionListOpen] = useState(false); // For question list modal
  const [isReviewCorrectModalOpen, setIsReviewCorrectModalOpen] = useState(false);
  const [reviewCorrectData, setReviewCorrectData] = useState([]);
  const [isResultModalOpen, setIsResultModalOpen] = useState(false);
  const [attempts, setAttempts] = useState([]);
  const [selectedAttemptNo, setSelectedAttemptNo] = useState(null);
  const [attemptAnswers, setAttemptAnswers] = useState({});
  const [animateKey, setAnimateKey] = useState(0);
  const [currentPage, setCurrentPage] = useState(2);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isOfflineModalOpen, setIsOfflineModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [quizMaxAttempts, setQuizMaxAttempts] = useState(null);
  const { darkMode } = useContext(ThemeContext);
  const { mode, customColors } = useContext(ThemeContext);
  const [quizAttemptId, setQuizAttemptId] = useState(null);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [remainingQuestions, setRemainingQuestions] = useState([]);
  const [attemptNumber, setAttemptNumber] = useState(null);
  // Add new state variables at the top of the component
  const [timeLeft, setTimeLeft] = useState(30); // Timer for current question (30 seconds)
  const [totalTimeLeft, setTotalTimeLeft] = useState(0); // Total timer for the entire quiz
  const [isTimeoutModalOpen, setIsTimeoutModalOpen] = useState(false); // Timeout modal stateconst [totalTime, setTotalTime] = useState(0); // Total quiz time
  const token = sessionStorage.getItem("authToken");
  const apiUrl = process.env.REACT_APP_BASE_URL;



  const renderContent = (html) => {
    if (!html) return null;

    // Split content into LaTeX and non-LaTeX parts
    const parts = html.split(/(\$\$.+?\$\$|\$.+?\$)/g);

    return (
      <span>
        {parts.map((part, index) => {
          // Handle block-level LaTeX ($$...$$)
          if (part.startsWith("$$") && part.endsWith("$$")) {
            const latex = part.slice(2, -2);
            try {
              const renderedLatex = katex.renderToString(latex, { displayMode: true });
              return (
                <span
                  key={index}
                  dangerouslySetInnerHTML={{ __html: renderedLatex }}
                  className="rendered-content block-latex"
                />
              );
            } catch (error) {
              console.error("Block LaTeX rendering error:", error);
              return <span key={index} className="error-content">{part}</span>;
            }
          }

          // Handle inline LaTeX ($...$)
          if (part.startsWith("$") && part.endsWith("$")) {
            const latex = part.slice(1, -1);
            try {
              const renderedLatex = katex.renderToString(latex, { displayMode: false });
              return (
                <span
                  key={index}
                  dangerouslySetInnerHTML={{ __html: renderedLatex }}
                  className="rendered-content inline-latex"
                />
              );
            } catch (error) {
              console.error("Inline LaTeX rendering error:", error);
              return <span key={index} className="error-content">{part}</span>;
            }
          }

          // Handle non-LaTeX content
          try {
            const sanitizedContent = sanitizeHtml(part, {
              allowedTags: ["b", "i", "em", "strong", "u", "sub", "sup", "s", "blockquote", "code", "ul", "ol", "li", "span"],
              allowedAttributes: {
                span: ["style"],
              },
              allowedStyles: {
                "*": {
                  color: [/.*/],
                  "background-color": [/.*/],
                },
              },
              transformTags: {
                'p': '', // Remove p tags but keep content
                'br': '', // Remove br tags
                'div': 'span',
              },
            });

            return (
              <span
                key={index}
                dangerouslySetInnerHTML={{ __html: sanitizedContent }}
                className="rendered-content inline-text"
              />
            );
          } catch (error) {
            console.error("HTML rendering error:", error);
            return <span key={index} className="error-content">{part}</span>;
          }
        })}
      </span>
    );
  };

  // const formatPercentage = (value) => (value ? `${Math.round(value)}%` : "0%");
  // Load images for IMAGE type questions
  // Update this useEffect in PracticePage
  // Draw canvas for IMAGE questions when they load
  // Add useEffect to ensure Start Quiz modal displays on navigation
  useEffect(() => {
  if (passedQuizId) {
    handleStartQuiz();
  }
}, []); 

  const handlePreviousQuestion = () => {
    setCurrentQuestionIndex((prev) => Math.max(prev - 1, 0));
  };

  // Add this component inside PracticePage, before the main return statement
  // Alternative version that uses stored canvas dimensions if available

  // handleNextQuestion function
  const handleNextQuestion = () => {
    const currentQuestion = questions[currentQuestionIndex];
    const userAnswer = userAnswers[currentQuestion.id];
    const isSkipped = questionStatuses[currentQuestionIndex] === "skipped";

    const isAnswered = (() => {
      if (userAnswer === undefined || userAnswer === null) return false;
      if (currentQuestion.type === "image") {
        return currentQuestion.labels?.every((label) => userAnswer[label.id]?.trim());
      }
      if (Array.isArray(userAnswer)) return userAnswer.length > 0;
      if (typeof userAnswer === "string") return userAnswer.trim() !== "";
      if (typeof userAnswer === "object") return Object.keys(userAnswer).length > 0;
      return false;
    })();

    // Disable Next button if the question is skipped and not answered
    if (!isAnswered && isSkipped) {
      return;
    }

    if (isAnswered || isSkipped) {
      setCurrentQuestionIndex((prev) => Math.min(prev + 1, questions.length - 1));
    }
  };

  useEffect(() => {
    const initialStatuses = {};
    questions.forEach((_, index) => {
      initialStatuses[index] = null; // null for unattempted
    });
    setQuestionStatuses(initialStatuses);
  }, [questions]);

  // Handle answer change with auto-save for all question types
  const handleAnswerChange = (questionId, value, isMCQ = false, labelId = null) => {
    setUserAnswers((prev) => {
      if (labelId !== null) {
        const currentImageAnswers = prev[questionId] || {};
        const newImageAnswers = {
          ...currentImageAnswers,
          [labelId]: value,
        };
        return {
          ...prev,
          [questionId]: newImageAnswers,
        };
      }

      // Handle MCQ
      if (isMCQ) {
        const currentAnswers = Array.isArray(prev[questionId]) ? prev[questionId] : [];
        if (currentAnswers.includes(value)) {
          const updatedAnswers = currentAnswers.filter((ans) => ans !== value);
          return {
            ...prev,
            [questionId]: updatedAnswers,
          };
        } else {
          return {
            ...prev,
            [questionId]: [...currentAnswers, value],
          };
        }
      }

      // Handle other types
      return {
        ...prev,
        [questionId]: value,
      };
    });

    const index = questions.findIndex((q) => q.id === questionId);

    // Update question status
    setQuestionStatuses((prev) => {
      if (labelId !== null) {
        // For IMAGE type, check if all labels are filled
        const question = questions[index];
        const currentImageAnswers = { ...(userAnswers[questionId] || {}), [labelId]: value };
        const allLabelsFilled = question.labels?.every((label) => currentImageAnswers[label.id]?.trim());
        return {
          ...prev,
          [index]: allLabelsFilled ? 'attempted' : null,
        };
      }
      return {
        ...prev,
        [index]: isMCQ
          ? (Array.isArray(userAnswers[questionId]) && userAnswers[questionId].length > 0 ? 'attempted' : null)
          : (value?.trim() !== '' ? 'attempted' : null),
      };
    });
  };

  const handleSkipQuestion = () => {
    setCurrentQuestionIndex((prev) => Math.min(prev + 1, questions.length - 1));
    setQuestionStatuses((prev) => ({
      ...prev,
      [currentQuestionIndex]: "skipped",
    }));
  };

  const handleReviewOpen = () => {
    setTempAnswers({ ...userAnswers });
    setReviewModalOpen(true);
  };

  const handleReviewClose = () => {
    setReviewModalOpen(false);
    setTempAnswers({});
  };

  // Function to fetch review correct answers
  const fetchReviewCorrectAnswers = async () => {
    if (!quizId || !quizAttemptId) {
      setError("Quiz ID or attempt ID is missing.");
      return;
    }
    try {
      const token = sessionStorage.getItem("authToken");
      if (!token) {
        throw new Error("No auth token found");
      }
      const response = await fetch(`${apiUrl}/quiz/${quizId}/review/${quizAttemptId}/`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch review answers: ${response.statusText}`);
      }
      const reviewData = await response.json();
      console.log("Review API response:", reviewData); // Debug log
    setReviewCorrectData(reviewData.attempt?.review || []);
setAttemptNumber(reviewData.attempt?.attempt_number ?? null);
      setIsReviewCorrectModalOpen(true);
      setError(null);
    } catch (error) {
      console.error("Error fetching review answers:", error.message);
      setError(`Failed to fetch review answers: ${error.message}`);
    }
  };

  // renderReviewCorrectAnswer function 
  const renderReviewCorrectAnswer = (item, index) => {
    const {
      question_id,
      statement,
      question_type,
      given_answer,
      correct_answer,
      explanation,
      is_correct,
      answers,
    } = item;

    const displayGivenAnswer = (answer) => {
      if (!answer) return "Not answered";
      if (typeof answer === "object" && !Array.isArray(answer)) {
        return Object.entries(answer).map(([labelId, labelAnswer]) => {
          const answerObj = answers.find(
            (a) => a.answer_text.toLowerCase() === labelAnswer.toLowerCase()
          );
          const isLabelCorrect = answerObj ? answerObj.is_correct === true : false;
          return (
            <Typography
              key={labelId}
              variant="body2"
              sx={{
                color: mode === "dark" ? "#ffffff" : "#1e3a8a",
                mb: 0.5,
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                backgroundColor: mode === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(30, 139, 175, 0.05)",
                p: 1,
                borderRadius: "4px",
              }}
            >
              <strong>Label {labelId}:</strong> {renderContent(labelAnswer)}
              {isLabelCorrect ? (
                <CheckCircle color="success" sx={{ fontSize: "1rem" }} />
              ) : (
                <Cancel sx={{ color: "red", fontSize: "1rem" }} />
              )}
            </Typography>
          );
        });
      }
      if (Array.isArray(answer)) {
        return answer.map((ans, i) => (
          <Typography
            key={i}
            variant="body2"
            sx={{
              color: mode === "dark" ? "#ffffff" : "#1e3a8a",
              mb: 0.5,
              backgroundColor: mode === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(30, 139, 175, 0.05)",
              p: 1,
              borderRadius: "4px",
            }}
          >
            {renderContent(ans)}
          </Typography>
        ));
      }
      return (
        <Typography
          variant="body2"
          sx={{
            color: mode === "dark" ? "#ffffff" : "#1e3a8a",
            backgroundColor: mode === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(30, 139, 175, 0.05)",
            p: 1,
            borderRadius: "4px",
          }}
        >
          {renderContent(answer)}
        </Typography>
      );
    };

    const displayCorrectAnswer =
      correct_answer === null ||
        correct_answer === "[]" ||
        correct_answer === ""
        ? "Not provided"
        : typeof correct_answer === "string"
          ? correct_answer.replace(/^\['|'\]$/g, "")
          : Array.isArray(correct_answer)
            ? correct_answer.join(", ")
            : String(correct_answer);

    const displayExplanation = explanation || "No explanation provided";
    const displayStatement = statement
      ? statement.replace(/\[BLANK\]/g, "______")
      : "No statement provided";

    return (
      <Box
        key={question_id || index}
        sx={{
          mb: 3,
          p: 2,
          borderRadius: "12px",
          border: `1px solid ${mode === "dark"
              ? "rgba(255, 255, 255, 0.1)"
              : "rgba(30, 139, 175, 0.1)"
            }`,
          backgroundColor: is_correct === true
            ? "rgba(76, 175, 80, 0.1)"
            : is_correct === false
              ? "rgba(244, 67, 54, 0.1)"
              : mode === "dark"
                ? "rgba(255, 255, 255, 0.05)"
                : "rgba(30, 139, 175, 0.05)",
        }}
      >
        <Typography
          variant="body1"
          sx={{
            mb: 1,
            color: mode === "dark" ? "#ffffff" : "#1e3a8a",
            fontWeight: 600,
          }}
        >
          <strong>({question_type}) Question {index + 1}:</strong>{" "}
          {renderContent(displayStatement)}
        </Typography>
        <Box
          sx={{
            mb: 1,
            p: 1.5,
            borderRadius: "8px",
            backgroundColor: mode === "dark" ? "#2d3748" : "#e3f2fd",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: mode === "dark" ? "#ffffff" : "#1e3a8a",
              fontWeight: 600,
            }}
          >
            <strong>Your Answer:</strong> {displayGivenAnswer(given_answer)}
          </Typography>
        </Box>
        <Typography
          variant="body2"
          sx={{
            mb: 1,
            color: mode === "dark" ? "#ffffff" : "#1e3a8a",
          }}
        >
          <strong>Correct Answer:</strong> {renderContent(displayCorrectAnswer)}
        </Typography>
        {question_type.toLowerCase() === "mcq" && answers && Array.isArray(answers) && (
          <Box sx={{ mt: 1 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: mode === "dark" ? "#ffffff" : "#1e3a8a",
              }}
            >
              Option Explanations:
            </Typography>
            {answers.map((option, optIndex) => (
              <Typography
                key={option.id || optIndex}
                variant="body2"
                sx={{
                  ml: 2,
                  color: mode === "dark" ? "#ffffff" : "#1e3a8a",
                }}
              >
                {renderContent(option.answer_text || "Option missing")}:{" "}
                {renderContent(option.explanation || "No explanation provided")}
              </Typography>
            ))}
          </Box>
        )}
      {question_type.toLowerCase() === "image" && answers && Array.isArray(answers) ? (
  <Box sx={{ mt: 1 }}>
    <Typography
      variant="body2"
      sx={{
        fontWeight: 600,
        color: mode === "dark" ? "#ffffff" : "#1e3a8a",
      }}
    >
      Label Explanations:
    </Typography>
    {answers.map((option, optIndex) => (
      <Typography
        key={option.id || optIndex}
        variant="body2"
        sx={{
          ml: 2,
          color: mode === "dark" ? "#ffffff" : "#1e3a8a",
        }}
      >
        <strong>Label {optIndex + 1}:</strong> {renderContent(option.answer_text || "Label missing")}{" "}
        {option.explanation && (
          <span>
            <strong>Explanation:</strong> {renderContent(option.explanation || "No explanation provided")}
          </span>
        )}
      </Typography>
    ))}
  </Box>
) : question_type.toLowerCase() !== "mcq" && (
  <Typography
    variant="body2"
    sx={{
      color: mode === "dark" ? "#ffffff" : "#1e3a8a",
      mt: 1,
    }}
  >
    <strong>Explanation:</strong> {renderContent(displayExplanation)}
  </Typography>
)}

      </Box>
    );
  };




  // Add useEffect to handle global timer logic
  useEffect(() => {
    let timer;
    if (isTimerEnabled && !isStartModalOpen && !results && !isTimeoutModalOpen) {
      setTotalTimeLeft(questions.length * 30); // Set total time (30s per question)
      timer = setInterval(() => {
        setTotalTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            setIsTimeoutModalOpen(true); // Show timeout modal when time is up
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer); // Cleanup on unmount or when conditions change
  }, [isTimerEnabled, isStartModalOpen, results, isTimeoutModalOpen, questions.length]);

  // Handle quiz start with API calls
 const handleStartQuiz = async () => {
  if (!passedQuizId) {
    setError("No quiz ID provided.");
    setIsStartModalOpen(false);
    setShouldShowStartModal(false);
    return;
  }
  setIsLoading(true);

  // Use settings from navigation state
  setNegativeMarking(passedNegMarking);
  setIsTimerEnabled(passedTimerEnabled);

  try {
    const questionsResponse = await fetch(`${apiUrl}/quiz/${passedQuizId}/questions/`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });
    if (!questionsResponse.ok) {
      throw new Error(`HTTP error! status: ${questionsResponse.status}`);
    }
    const questionsData = await questionsResponse.json();

    const mappedQuestions = questionsData.questions.map((q) => {
      const questionType = q.question_type.toLowerCase();
      return {
        id: q.id,
        statement: q.statement || q.question_text || q.text || "Untitled Question",
        type: questionType === "truefalse" ? "tf" : questionType,
        image: q.image || null,
        file_url: q.file_url || null,
        gridfs_id: q.gridfs_id || null,
        labels: q.labels || [],
        canvas_width: q.canvas_width || null,
        canvas_height: q.canvas_height || null,
        options:
          questionType === "truefalse"
            ? [{ id: "true", answer_text: "True" }, { id: "false", answer_text: "False" }]
            : questionType === "mcq" && q.answers
            ? q.answers.map((ans) => ({ id: ans.id, answer_text: ans.answer_text || ans.answer }))
            : [],
        rawAnswers: q.answers || [],
        correct_answer: q.correct_answer || q.answer || q.correctAnswer || q.solution || "",
        tags: q.tags || [],
        answers: questionType === "image"
          ? q.answers.map((ans) => ({ answer_text: ans }))
          : questionType === "fib"
            ? q.answers.map((ans) => ({ id: ans.id, answer_text: ans.answer }))
            : [],
      };
    });

    setQuizId(passedQuizId);
    setQuestions(mappedQuestions);

    const initialAnswers = {};
    const initialStatuses = {};
    mappedQuestions.forEach((q, index) => {
      initialAnswers[q.id] = q.type === "image" ? {} : "";
      initialStatuses[index] = null;
    });
    setUserAnswers(initialAnswers);
    setQuestionStatuses(initialStatuses);
    setIsStartModalOpen(false);
    setShouldShowStartModal(false);

    if (passedTimerEnabled) {
      setTotalTimeLeft(mappedQuestions.length * 30);
    }
  } catch (error) {
    console.error("Error fetching questions:", error);
    setError(`Failed to fetch questions: ${error.message}`);
    setQuestions([]);
  } finally {
    setIsLoading(false);
  }
};


  // Function to save answers to cache
  const saveAnswersToCache = async (answers) => {
    try {
      const cache = await caches.open('quiz-answers-cache');
      await cache.put(
        '/quiz-answers',
        new Response(JSON.stringify(answers), {
          headers: { 'Content-Type': 'application/json' },
        })
      );
    } catch (error) {
      console.error('Error saving answers to cache:', error);
    }
  };

  // Function to retrieve answers from cache
  const getAnswersFromCache = async () => {
    try {
      const cache = await caches.open('quiz-answers-cache');
      const response = await cache.match('/quiz-answers');
      if (response) {
        return await response.json();
      }
      return null;
    } catch (error) {
      console.error('Error retrieving answers from cache:', error);
      return null;
    }
  };

  const sanitizeAnswer = (answer) => {
    if (!answer) return "";
    return sanitizeHtml(answer, {
      allowedTags: ["b", "i", "em", "strong", "u", "sub", "sup", "p", "br", "blockquote", "code", "ul", "ol", "li"],
      allowedAttributes: {},
    });
  };



  // New submitQuiz function
  const submitQuiz = async () => {
  if (isSubmitting) return;
  setIsSubmitting(true);
  
  if (!navigator.onLine) {
    await saveAnswersToCache(userAnswers);
    setIsOfflineModalOpen(true);
    setIsSubmitting(false);
    return;
  }
    const submitUrl = `${apiUrl}/quiz/${quizId}/submit/`;
  const payload = {
    answers: questions
      .map((question) => {
        const questionId = question.id;
        const questionTypeMap = {
          fib: "FILL",
          mcq: "MCQ",
          tf: "TF",
          sub: "SUB",
          image: "IMAGE", // Add IMAGE mapping
        };
         const questionType = questionTypeMap[question.type.toLowerCase()] || question.type.toUpperCase();
        const selectedAnswer = userAnswers[question.id];

 // Skip if no answer
        if (!selectedAnswer ||
          (Array.isArray(selectedAnswer) && selectedAnswer.length === 0) ||
          (typeof selectedAnswer === "string" && selectedAnswer.trim() === "") ||
          (typeof selectedAnswer === "object" && !Array.isArray(selectedAnswer) && Object.keys(selectedAnswer).length === 0)) {
          return null;
        }

         
        let answerArray = [];

        // Handle IMAGE type

        if (question.type.toLowerCase() === "image") {
          return {
            question_id: questionId,
            question_type: questionType,
            selected_answer: selectedAnswer, // Send as object {1: "answer1", 2: "answer2"}
          };
        }

          // Handle MCQ
             if (question.type.toLowerCase() === "mcq") {
          const mcqOptions = question.options || question.choices || question.answers || [];
          answerArray = Array.isArray(selectedAnswer)
            ? selectedAnswer.map((ans) => {
              const option = mcqOptions.find((opt) => {
                const optText = opt.answer_text || opt.text || opt;
                return optText === ans || String(optText).toLowerCase() === String(ans).toLowerCase();
              });
                  return option ? option.answer_text : ans;
            })
            : [selectedAnswer];
        } else {
          // Handle other types
          if (Array.isArray(selectedAnswer)) {
            answerArray = selectedAnswer.map((ans) => sanitizeAnswer(ans));
          } else if (typeof selectedAnswer === "string" && selectedAnswer.includes(",")) {
            answerArray = selectedAnswer
              .split(",")
              .map((ans) => sanitizeAnswer(ans.trim()))
              .filter(Boolean);
          } else {
            answerArray = [sanitizeAnswer(selectedAnswer)];
          }
        }

            return {
          question_id: questionId,
          question_type: questionType,
          selected_answer: answerArray,
        };
      })
      .filter((item) => item !== null),
  };
  
  if (payload.answers.length === 0) {
    payload.answers = [];
  }
  try {
    const submitResponse = await fetch(submitUrl, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });
          if (!submitResponse.ok) {
      const errorText = await submitResponse.text();
      let errorMessage = `Failed to submit quiz: ${submitResponse.status} ${errorText}`;
      try {
        const errorData = JSON.parse(errorText);
        errorMessage = errorData.detail || errorData.message || errorText;
        if (submitResponse.status === 401) {
          navigate("/login");
        } else if (submitResponse.status === 400) {
          errorMessage = errorData.detail || "Invalid submission data. Please check your answers.";
        }
      } catch { }
        throw new Error(errorMessage);
      }
      const data = await submitResponse.json();
    setQuizAttemptId(data.quiz_attempt_id);
      const calculatedResults = {
      // Use API response values directly
      attempted_questions: data.attempted_questions || 0,
      total_questions: data.total_questions || questions.length,
      correct_answers: data.correct_answers || 0,
      wrong_answers: data.wrong_answers || 0,
      
      // Determine final_score based on negative marking setting
      final_score: negativeMarking 
        ? data.final_score  // Use final_score when negative marking is on
        : data.correct_answers,  // Use correct_answers when negative marking is off
      
      // Use the appropriate score percentage
      score_percentage: negativeMarking
        ? data.score_percentage  // Use score_percentage when negative marking is on
        : data.no_negative_marking_score_percentage,  // Use no_negative_marking_score_percentage when off
      
      // Use the appropriate result
      result: negativeMarking
        ? data.result  // Use result when negative marking is on
        : data.no_negative_result,  // Use no_negative_result when off
      
      // Other fields from API
      passing_percentage: data.passing_percentage || passingPercentage || 40,
      attempts_used: data.attempt_number || data.attempt_no || (attempts.length + 1),
      quiz_status: data.quiz_status || "Completed",
      started_at: data.started_at || new Date().toISOString(),
      ended_at: data.ended_at || new Date().toISOString(),
    };

    setResults(calculatedResults);
    setSelectedAttemptNo(calculatedResults.attempts_used);
    setAttemptAnswers((prev) => ({
      ...prev,
      [calculatedResults.attempts_used]: { ...userAnswers },
    }));
    
    // Store in localStorage
          try {
      const newAttempt = {
        attempt_no: calculatedResults.attempts_used,
        quiz_name: `Practice Quiz ${quizId}`,
        results: {
          score_percentage: calculatedResults.score_percentage,
          attempted_questions: calculatedResults.attempted_questions,
          time_spent: 0,
          final_score: calculatedResults.final_score,
          correct_answers: calculatedResults.correct_answers,
          wrong_answers: calculatedResults.wrong_answers,
          result: calculatedResults.result,
        },
        answers: Object.values(userAnswers),
        timestamp: new Date().toISOString(),
        quiz_type: "Practice",
      };
            const existingHistory = localStorage.getItem("quizHistory");
      const history = existingHistory ? JSON.parse(existingHistory) : {};
      const existingAttempts = history[quizId] || [];
      const attemptExists = existingAttempts.some((attempt) => attempt.attempt_no === calculatedResults.attempts_used);
         
      if (!attemptExists) {
        history[quizId] = [...existingAttempts, newAttempt];
        localStorage.setItem("quizHistory", JSON.stringify(history));
      }
      
      const existingQuizIds = localStorage.getItem("quizIds");
      const quizIds = existingQuizIds ? JSON.parse(existingQuizIds) : [];
      if (!quizIds.includes(quizId)) {
        quizIds.push(quizId);
        localStorage.setItem("quizIds", JSON.stringify(quizIds));
      }
    } catch (error) {
      console.error("Error storing quiz history in localStorage:", error);
    }
       const updatedAttempts = await fetchAvailableAttempts(calculatedResults.attempts_used);
    setAttempts(updatedAttempts);
    setError(null);
    setAnimateKey((prev) => prev + 1);
  } catch (error) {
    console.error("Error submitting quiz:", error.message);
    setError(`Failed to submit quiz: ${error.message}`);
    setResults(null);
  } finally {
    setIsSubmitting(false);
    setOpenConfirmDialog(false);
  }
};

  // 👇 ab handleSubmitQuiz me sirf unanswered check rahega
  const handleSubmitQuiz = async () => {
    if (!quizId) {
      console.error("No quiz ID available. Start the quiz first.");
      setError("No quiz ID available. Start the quiz first.");
      setIsResultModalOpen(true);
      return;
    }

    if (isSubmitting) return;

    // Updated unanswered check
    const unanswered = questions.filter((q) => {
      const userAnswer = userAnswers[q.id];
      if (!userAnswer) return true; // Unanswered (undefined or null)
      if (q.type === 'image') {
        return !q.labels?.every(label => userAnswer[label.id]?.trim());
      }

      if (Array.isArray(userAnswer)) return userAnswer.length === 0;
      if (typeof userAnswer === "string") return userAnswer.trim() === "";
      if (typeof userAnswer === 'object') return Object.keys(userAnswer).length === 0;
      return true;
    });

    if (unanswered.length > 0) {
      setRemainingQuestions(unanswered);
      setOpenConfirmDialog(true);
      return;
    }

    await submitQuiz();
  };

  const sanitizeHtmlConfig = {
    allowedTags: [
      "b", "i", "em", "strong", "u", "sub", "sup", "blockquote", "code",
      "ul", "ol", "li", "span", "s", "div", "h1", "h2", "h3", "h4", "h5", "h6"
    ],
    allowedAttributes: {
      span: ["style"],
      div: ["style"],
    },
    allowedStyles: {
      "*": {
        color: [/.*/],
        "background-color": [/.*/],
        "font-size": [/.*/],
        "text-align": [/^left$/, /^right$/, /^center$/, /^justify$/],
      },
    },
    disallowedTagsMode: "discard",
    transformTags: {
      p: "", // Remove <p> tags but keep content
      br: "", // Remove <br> tags but keep content
    },
  };

  const renderSanitizedContent = (html) => {
    if (!html) return null;
    const sanitizedHtml = sanitizeHtml(html, sanitizeHtmlConfig);
    return <span dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />;
  };


  const formatQuestionText = (question) => {
    const rawText = question.question || question.text || question.statement || "Untitled Question";
    const sanitizedText = sanitizeHtml(rawText, sanitizeHtmlConfig);
    return sanitizedText.replace(/\[BLANK\]/gi, "_____");
  };


  // Modified submitCachedAnswers to fix MCQ answer submission
  const submitCachedAnswers = async () => {
    const cachedAnswers = await getAnswersFromCache();
    if (cachedAnswers && quizId && !isSubmitting) {
      setIsSubmitting(true);
      const submitUrl = `${apiUrl}/quiz/${quizId}/submit/`;
      const payload = {
        answers: questions.map((question, index) => {
          const questionId = Number(question.id) || index + 1;
          let selectedAnswer = cachedAnswers[question.id] ? cachedAnswers[question.id] : (question.type === "mcq" ? [] : "");
          if (question.type === "mcq") {
            const mcqOptions = question.options || question.choices || question.answers || [];
            selectedAnswer = Array.isArray(selectedAnswer)
              ? selectedAnswer.map((ans) => {
                const optionIndex = mcqOptions.findIndex(
                  (opt) => (opt.answer_text || opt.text || opt) === ans
                );
                return optionIndex >= 0 ? optionIndex.toString() : sanitizeAnswer(ans);
              })
              : [];
            if (selectedAnswer.length === 0) {
              console.warn(`No valid answers for question ${questionId}`);
              selectedAnswer = [];
            }
          } else {
            selectedAnswer = sanitizeAnswer(selectedAnswer);
          }
          return {
            question_id: questionId,
            selected_answer: Array.isArray(selectedAnswer) ? selectedAnswer : [selectedAnswer],
          };
        }),
      };
      try {
        console.log("Submitting cached to:", submitUrl);
        console.log("Cached Payload:", JSON.stringify(payload, null, 2));
        console.log("Raw cachedAnswers:", JSON.stringify(cachedAnswers, null, 2));
        const submitResponse = await fetch(submitUrl, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        });
        if (!submitResponse.ok) {
          const errorText = await submitResponse.text();
          console.error("API Error Response (Cached):", errorText);
          let errorMessage = `Failed to submit cached answers: ${submitResponse.status} ${errorText}`;
          try {
            const errorData = JSON.parse(errorText);
            errorMessage = errorData.detail || errorData.message || errorText;
            if (submitResponse.status === 401) {
              navigate("/login");
            } else if (submitResponse.status === 400) {
              errorMessage = errorData.detail || "Invalid submission data. Please check your answers.";
            }
          } catch {
            // Non-JSON error response
          }
          throw new Error(errorMessage);
        }
        const data = await submitResponse.json();
        console.log("API Success Response (Cached):", JSON.stringify(data, null, 2));
        let calculatedResults = { ...data };
        const passingPercentageValue = results?.passing_percentage || passingPercentage || 35;
        const currentAttemptNo = data.attempt_number || data.attempt_no || (attempts.length + 1);
        let correctAnswers = 0;
        let wrongAnswers = 0;
        const attemptedQuestions = Object.values(cachedAnswers).filter(
          (answer) => (Array.isArray(answer) && answer.length > 0) || (typeof answer === "string" && answer.trim() !== "")
        ).length;
        questions.forEach((question) => {
          const userAnswer = cachedAnswers[question.id] || (question.type === "mcq" ? [] : "");
          let correctAnswer = "";
          if (question.type === "mcq") {
            const correctOptions = (question.options || question.choices || question.answers || []).filter(
              (opt) => opt.is_correct || opt.correct || opt.isCorrect
            ).map((opt) => opt.answer_text || opt.text || "");
            correctAnswer = correctOptions;
            const normalizedUserAnswer = Array.isArray(userAnswer)
              ? userAnswer.map((ans) => String(ans).trim().toLowerCase().replace(/\s+/g, " "))
              : [];
            const normalizedCorrectAnswer = Array.isArray(correctAnswer)
              ? correctAnswer.map((ans) => String(ans).trim().toLowerCase().replace(/\s+/g, " "))
              : [];
            const isCorrect = normalizedUserAnswer.length === normalizedCorrectAnswer.length &&
              normalizedUserAnswer.every((ans) => normalizedCorrectAnswer.includes(ans)) &&
              normalizedCorrectAnswer.every((ans) => normalizedUserAnswer.includes(ans));
            if (Array.isArray(userAnswer) && userAnswer.length > 0) {
              if (isCorrect) correctAnswers += 1;
              else wrongAnswers += 1;
            }
          } else {
            correctAnswer =
              question.correct_answer ||
              question.answer ||
              question.correctAnswer ||
              question.solution ||
              "";
            const normalizedUserAnswer = String(userAnswer).trim().toLowerCase().replace(/\s+/g, " ");
            const normalizedCorrectAnswer = String(correctAnswer).trim().toLowerCase().replace(/\s+/g, " ");
            const isCorrect =
              question.type === "tf"
                ? normalizedUserAnswer === normalizedCorrectAnswer ||
                (normalizedUserAnswer === "true" && normalizedCorrectAnswer === "1") ||
                (normalizedUserAnswer === "false" && normalizedCorrectAnswer === "0")
                : normalizedUserAnswer === normalizedCorrectAnswer;
            if (userAnswer && userAnswer.trim() !== "") {
              if (isCorrect) correctAnswers += 1;
              else wrongAnswers += 1;
            }
          }
        });
        calculatedResults.final_score = negativeMarking ? correctAnswers - wrongAnswers : correctAnswers;
        calculatedResults.attempted_questions = attemptedQuestions;
        calculatedResults.score_percentage = questions.length > 0 ? (calculatedResults.final_score / questions.length) * 100 : 0;
        calculatedResults.result = calculatedResults.score_percentage >= passingPercentageValue ? "Pass" : "Fail";
        calculatedResults.attempts_used = currentAttemptNo;
        calculatedResults.total_questions = questions.length;
        calculatedResults.passing_percentage = passingPercentageValue;
        calculatedResults.correct_answers = correctAnswers;
        calculatedResults.wrong_answers = wrongAnswers;
        setResults(calculatedResults);
        setSelectedAttemptNo(currentAttemptNo);
        setAttemptAnswers((prev) => ({
          ...prev,
          [currentAttemptNo]: { ...cachedAnswers },
        }));
        try {
          const newAttempt = {
            attempt_no: currentAttemptNo,
            quiz_name: `Practice Quiz ${quizId}`,
            results: {
              score_percentage: calculatedResults.score_percentage || 0,
              attempted_questions: calculatedResults.attempted_questions || attemptedQuestions,
              time_spent: calculatedResults.time_spent || 0,
              final_score: calculatedResults.final_score || 0,
              correct_answers: calculatedResults.correct_answers || 0,
              wrong_answers: calculatedResults.wrong_answers || 0,
              result: calculatedResults.result || 'Unknown'
            },
            answers: Object.values(cachedAnswers),
            timestamp: new Date().toISOString(),
            quiz_type: 'Practice'
          };
          const existingHistory = localStorage.getItem('quizHistory');
          const history = existingHistory ? JSON.parse(existingHistory) : {};
          const existingAttempts = history[quizId] || [];
          const attemptExists = existingAttempts.some(attempt => attempt.attempt_no === currentAttemptNo);
          if (!attemptExists) {
            history[quizId] = [...existingAttempts, newAttempt];
            localStorage.setItem('quizHistory', JSON.stringify(history));
          }
          const existingQuizIds = localStorage.getItem('quizIds');
          const quizIds = existingQuizIds ? JSON.parse(existingQuizIds) : [];
          if (!quizIds.includes(quizId)) {
            quizIds.push(quizId);
            localStorage.setItem('quizIds', JSON.stringify(quizIds));
          }
        } catch (error) {
          console.error('Error storing cached quiz history in localStorage:', error);
        }
        const updatedAttempts = await fetchAvailableAttempts(currentAttemptNo);
        setAttempts(updatedAttempts);
        setError(null);
        setAnimateKey((prev) => prev + 1);
        console.log("Cached answers submitted successfully:", calculatedResults);
        const cache = await caches.open('quiz-answers-cache');
        await cache.delete('/quiz-answers');
      } catch (error) {
        console.error("Error submitting cached answers:", error.message);
        setError(`Failed to submit cached answers: ${error.message}`);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  // Add useEffect to handle online/offline events
  useEffect(() => {
    const handleOnline = async () => {
      setIsOfflineModalOpen(false);
      await submitCachedAnswers();
    };

    window.addEventListener('online', handleOnline);

    return () => {
      window.removeEventListener('online', handleOnline);
    };
  }, [quizId, questions, userAnswers, results, passingPercentage, attempts, negativeMarking]);

  const handleRetryQuiz = () => {
    setResults(null);
    setError(null);
    setUserAnswers({});
    setCurrentQuestionIndex(0);
    setQuestionStatuses({}); // Reset question statuses
    setIsResultModalOpen(false); // Close the results modal
    setSelectedAttemptNo(null); // Reset selected attempt to ensure new attempt is fetched
    // Preserve quizId and attempts to maintain attempt history
  };

  // --- Cancel Quiz Logic ---
  const handleCancelQuiz = () => {
    const isBeforeStart = isStartModalOpen && shouldShowStartModal;
    const isAfterFinish = Boolean(results);
    const isNoQuestions = !questions || questions.length === 0;

    if (isBeforeStart || isAfterFinish || isNoQuestions) {
      // Direct navigation if quiz not started, already finished, or no questions
      navigate("/allquiz");
    } else {
      // During quiz with questions
      setIsConfirmModalOpen(true);
    }
  };


  // Handle confirm action for canceling quiz
  const handleConfirmCancel = () => {
    setQuizId(null);
    setResults(null);
    setError(null);
    setUserAnswers({});
    setIsConfirmModalOpen(false); // Close confirmation modal
    navigate("/allquiz");
  };

  // Handle cancel action for confirmation modal
  const handleCancelConfirmModal = () => {
    setIsConfirmModalOpen(false); // Close confirmation modal without resetting
  };

  // Fetch detailed results from API
  const fetchDetailedResults = async () => {
    if (!quizId) {
      setError("Quiz ID is missing. Cannot fetch results.");
      return null;
    }
    try {
      const token = sessionStorage.getItem("authToken");
      if (!token) {
        throw new Error("No auth token found");
      }
      const response = await fetch(`${apiUrl}/quiz/${quizId}/result/`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch detailed results: ${response.statusText}`);
      }
      const resultData = await response.json();
      return resultData;
    } catch (error) {
      setError(`Failed to fetch detailed results: ${error.message}`);
      return null;
    }
  };

  // Fetch all available attempts for the quiz
  const fetchAvailableAttempts = async (maxAttemptNo) => {
    if (!quizId) {
      setError("Quiz ID is missing. Cannot fetch attempts.");
      return [];
    }
    try {
      const token = sessionStorage.getItem("authToken");
      if (!token) {
        throw new Error("No auth token found");
      }
      const maxAttemptsValue = quizMaxAttempts || maxAttempts || Infinity; // Use Infinity for unlimited attempts
      const attemptLimit = Math.min(maxAttemptNo || attempts.length + 1, maxAttemptsValue); // Limit to current attempt number
      const attemptPromises = [];
      for (let i = 1; i <= attemptLimit; i++) {
        attemptPromises.push(
          fetch(`${apiUrl}/quiz/${quizId}/attempt/${i}/`, {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          })
            .then(async (response) => {
              if (response.ok) {
                const data = await response.json();
                return { attempt_no: i, data };
              }
              return null;
            })
            .catch(() => null)
        );
      }
      const results = await Promise.all(attemptPromises);
      const validAttempts = results
        .filter((result) => result !== null)
        .map((result) => ({
          attempt_no: result.attempt_no,
          data: result.data,
        }))
        .sort((a, b) => a.attempt_no - b.attempt_no);
      return validAttempts;
    } catch (error) {
      setError(`Failed to fetch attempts: ${error.message}`);
      console.error("Fetch attempts error:", error);
      return [];
    }
  };


  // Fetch specific attempt details
  const fetchAttemptDetails = async (attemptNo) => {
    if (!quizId || !attemptNo) {
      setError("Quiz ID or attempt number is missing.");
      return null;
    }
    try {
      const token = sessionStorage.getItem("authToken");
      if (!token) {
        throw new Error("No auth token found");
      }
      const response = await fetch(`${apiUrl}/quiz/${quizId}/attempt/${attemptNo}/`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch attempt ${attemptNo}: ${response.statusText}`);
      }
      const resultData = await response.json();
      setAttemptAnswers((prev) => ({
        ...prev,
        [attemptNo]: resultData.answers
          ? resultData.answers.reduce((acc, ans) => {
            acc[ans.question_id] = ans.selected_answer || "";
            return acc;
          }, {})
          : {},
      }));
      return resultData;
    } catch (error) {
      setError(`Failed to fetch attempt ${attemptNo}: ${error.message}`);
      return null;
    }
  };

  // Modified handleViewResult to fix MCQ answer validation
  // Modified handleViewResult to preserve existing results
  const handleViewResult = async () => {
    const currentAttemptNo = results?.attempts_used || attempts.length + 1;
    const availableAttempts = await fetchAvailableAttempts(currentAttemptNo);
    setAttempts(availableAttempts);

    let selectedAttemptNum = null;
    let attemptSpecificAnswers = {};

    if (availableAttempts.length > 0) {
      const latestAttempt = availableAttempts[availableAttempts.length - 1];
      selectedAttemptNum = latestAttempt.attempt_no;
      setSelectedAttemptNo(selectedAttemptNum);

      attemptSpecificAnswers = attemptAnswers[selectedAttemptNum] || {};
      if (Object.keys(attemptSpecificAnswers).length === 0) {
        const attemptDetails = await fetchAttemptDetails(selectedAttemptNum);
        attemptSpecificAnswers = attemptDetails?.answers
          ? attemptDetails.answers.reduce((acc, ans) => {
            acc[ans.question_id] = ans.selected_answer || "";
            return acc;
          }, {})
          : userAnswers;
        setAttemptAnswers((prev) => ({
          ...prev,
          [selectedAttemptNum]: attemptSpecificAnswers,
        }));
      }
    } else {
      selectedAttemptNum = 1;
      setSelectedAttemptNo(1);
      attemptSpecificAnswers = userAnswers;
    }

    // Use existing results if available, instead of recalculating
    if (!results) {
      setError("No results available to display.");
      setIsResultModalOpen(true);
      setAnimateKey((prev) => prev + 1);
      return;
    }

    // Fetch detailed results only to update metadata if needed
    const resultData = await fetchDetailedResults();
    const updatedResults = {
      ...results,
      started_at: resultData?.started_at ?? results.started_at ?? "N/A",
      ended_at: resultData?.ended_at ?? results.ended_at ?? "N/A",
      quiz_status: resultData?.quiz_status ?? results.quiz_status ?? "N/A",
      max_attempts: resultData?.max_attempts ?? quizMaxAttempts ?? null,
    };

    setResults(updatedResults);
    setQuizMaxAttempts(resultData?.max_attempts ?? quizMaxAttempts ?? null);
    setIsResultModalOpen(true);
    setAnimateKey((prev) => prev + 1);
    setError(null);
  };

  // Modified handleAttemptChange to fix MCQ answer validation
  // Modified handleAttemptChange to fix MCQ answer validation and final_score calculation
  const handleAttemptChange = async (event) => {
  const attemptNo = Number(event.target.value);
  setSelectedAttemptNo(attemptNo);

  const attemptData = await fetchAttemptDetails(attemptNo);
  if (!attemptData) {
    setAnimateKey((prev) => prev + 1);
    return;
  }

  let attemptSpecificAnswers = attemptAnswers[attemptNo] || {};
  if (Object.keys(attemptSpecificAnswers).length === 0) {
    attemptSpecificAnswers = attemptData.answers
      ? attemptData.answers.reduce((acc, ans) => {
        acc[ans.question_id] = ans.selected_answer || "";
        return acc;
      }, {})
      : userAnswers;
  }


    // Calculate correct and wrong answers if not provided
    let correctAnswers = attemptData.correct_answers || 0;
    let wrongAnswers = attemptData.wrong_answers || 0;
    if (attemptData.correct_answers === undefined || attemptData.wrong_answers === undefined) {
      correctAnswers = 0;
      wrongAnswers = 0;
      questions.forEach((question) => {
        const userAnswer = attemptSpecificAnswers[question.id] || (question.type === "mcq" ? [] : "");
        let correctAnswer = "";
        if (question.type === "mcq") {
          const correctOptions = (question.options || question.choices || question.answers || []).filter(
            (opt) => opt.is_correct || opt.correct || opt.isCorrect
          ).map((opt) => opt.answer_text || opt.text || "");
          correctAnswer = correctOptions;
          const normalizedUserAnswer = Array.isArray(userAnswer)
            ? userAnswer.map((ans) => String(ans).trim().toLowerCase().replace(/\s+/g, " "))
            : [];
          const normalizedCorrectAnswer = Array.isArray(correctAnswer)
            ? correctAnswer.map((ans) => String(ans).trim().toLowerCase().replace(/\s+/g, " "))
            : [];
          const isCorrect =
            normalizedUserAnswer.length === normalizedCorrectAnswer.length &&
            normalizedUserAnswer.every((ans) => normalizedCorrectAnswer.includes(ans)) &&
            normalizedCorrectAnswer.every((ans) => normalizedUserAnswer.includes(ans));
          if (Array.isArray(userAnswer) && userAnswer.length > 0) {
            if (isCorrect) correctAnswers += 1;
            else wrongAnswers += 1;
          }
        } else {
          correctAnswer =
            question.correct_answer ||
            question.answer ||
            question.correctAnswer ||
            question.solution ||
            "";
          const normalizedUserAnswer = String(userAnswer).trim().toLowerCase().replace(/\s+/g, " ");
          const normalizedCorrectAnswer = String(correctAnswer).trim().toLowerCase().replace(/\s+/g, " ");
          const isCorrect =
            question.type === "tf"
              ? normalizedUserAnswer === normalizedCorrectAnswer ||
              (normalizedUserAnswer === "true" && normalizedCorrectAnswer === "1") ||
              (normalizedUserAnswer === "false" && normalizedCorrectAnswer === "0")
              : normalizedUserAnswer === normalizedCorrectAnswer;
          if (userAnswer && userAnswer.trim() !== "") {
            if (isCorrect) correctAnswers += 1;
            else wrongAnswers += 1;
          }
        }
      });
    }

    // Calculate attemptedQuestions
    const attemptedQuestions = Object.values(attemptSpecificAnswers).filter(
      (answer) => (Array.isArray(answer) && answer.length > 0) || (typeof answer === "string" && answer.trim() !== "")
    ).length;

    const totalQuestions = questions.length;
    let finalScore, scorePercentage;
    if (!negativeMarking) {
      // When negative marking is off, final_score is the number of correct answers
      finalScore = correctAnswers;
      // score_percentage is (correct_answers / total_questions) * 100
      scorePercentage = totalQuestions > 0 ? (correctAnswers / totalQuestions) * 100 : 0;
    } else {
      // When negative marking is on, map from response or calculate as correct - wrong
      finalScore = attemptData.final_score !== undefined
        ? attemptData.final_score
        : (correctAnswers - wrongAnswers);
      scorePercentage = attemptData.score_percentage !== undefined
        ? attemptData.score_percentage
        : (totalQuestions > 0 ? ((correctAnswers - wrongAnswers) / totalQuestions) * 100 : 0);
    }

    const passingPercentageValue =
      attemptData.passing_percentage ?? results?.passing_percentage ?? passingPercentage ?? 35;
    const result = scorePercentage >= passingPercentageValue ? "Pass" : "Fail";

    const newResults = {
      // final_score: finalScore,
      // score_percentage: scorePercentage,
      // result: result,
      // attempted_questions: attemptData.attempted_questions ?? attemptedQuestions,
      attempts_used: attemptData.attempt_no ?? attemptData.attempt_number ?? attemptNo,
      // correct_answers: correctAnswers,
      // wrong_answers: wrongAnswers,
      // started_at: attemptData.started_at ?? results?.started_at ?? "N/A",
      // ended_at: attemptData.ended_at ?? results?.ended_at ?? "N/A",
      // quiz_status: attemptData.quiz_status ?? results?.quiz_status ?? "N/A",
      // total_questions: totalQuestions,
      // passing_percentage: passingPercentageValue,
        // Use values from attemptData directly
    attempted_questions: attemptData.attempted_questions,
    total_questions: attemptData.total_questions || questions.length,
    correct_answers: attemptData.correct_answers,
    wrong_answers: attemptData.wrong_answers,
    
    // Use final_score based on negative marking
    final_score: negativeMarking
      ? attemptData.final_score
      : attemptData.correct_answers,
    
    // Use appropriate score percentage
    score_percentage: negativeMarking
      ? attemptData.score_percentage
      : attemptData.no_negative_marking_score_percentage,
    
    // Use appropriate result
    result: negativeMarking
      ? attemptData.result
      : attemptData.no_negative_result,
    
    // Other fields
    passing_percentage: attemptData.passing_percentage,
    attempts_used: attemptData.attempt_no || attemptData.attempt_number || attemptNo,
    started_at: attemptData.started_at,
    ended_at: attemptData.ended_at,
    quiz_status: attemptData.quiz_status,
  };

   setResults(newResults);
  setQuizMaxAttempts(attemptData.max_attempts ?? quizMaxAttempts ?? null);
  setAnimateKey((prev) => prev + 1);
  setError(null);
  };

  // Format Percentage
  const formatPercentage = (percentage) => {
    if (percentage === null || percentage === undefined) return "N/A";
    const num = Number(percentage);
    if (Number.isInteger(num)) return `${num}%`;
    return `${num.toFixed(num % 1 === 0 ? 0 : num < 10 ? 2 : 1)}%`;
  };

  // Result Fields for Results Modal
  const resultFields = results
    // ? [
    //   { label: "Result", value: `${results.result} (Passing: ${formatPercentage(results.passing_percentage)})`, icon: CheckIcon, bgcolor: results.result.toLowerCase() === "pass" ? "#e8f5e9" : "#ffebee", color: results.result.toLowerCase() === "pass" ? "#2e7d32" : "#d32f2f" },
    //   { label: "Attempted Questions", value: `${results.attempted_questions} out of ${results.total_questions}`, icon: CheckIcon },
    //   { label: "Correct Answers", value: results.correct_answers, icon: CorrectIcon },
    //   { label: "Wrong Answers", value: results.wrong_answers, icon: IncorrectIcon },
    //   { label: "Final Score", value: `${results.final_score} out of ${results.total_questions}`, icon: TrophyIcon },
    //   { label: "Score Percentage", value: formatPercentage(results.score_percentage), icon: CheckIcon },
    //   { label: "Total Questions", value: results.total_questions, icon: InfoIcon },
    //   { label: "Attempts Used", value: `Attempt ${results.attempts_used} out of ${maxAttempts || "Unlimited"}`, icon: InfoIcon },
    //   { label: "Started At", value: results.started_at !== "N/A" ? new Date(results.started_at).toLocaleString("en-US", { hour: "numeric", minute: "numeric", hour12: true, day: "2-digit", month: "short", year: "numeric" }) : "N/A", icon: TimeIcon },
    //   { label: "Ended At", value: results.ended_at !== "N/A" ? new Date(results.ended_at).toLocaleString("en-US", { hour: "numeric", minute: "numeric", hour12: true, day: "2-digit", month: "short", year: "numeric" }) : "N/A", icon: TimeIcon },
    // ]
    // : [];

     ? [
      { 
        label: "Result", 
        value: `${results.result} (Passing: ${formatPercentage(results.passing_percentage)})`, 
        icon: CheckIcon, 
        bgcolor: results.result?.toLowerCase() === "pass" ? "#e8f5e9" : "#ffebee", 
        color: results.result?.toLowerCase() === "pass" ? "#2e7d32" : "#d32f2f" 
      },
      { 
        label: "Attempted Questions", 
        value: `${results.attempted_questions} out of ${results.total_questions}`, 
        icon: CheckIcon 
      },
      { 
        label: "Correct Answers", 
        value: results.correct_answers, 
        icon: CorrectIcon 
      },
      { 
        label: "Wrong Answers", 
        value: results.wrong_answers, 
        icon: IncorrectIcon 
      },
      { 
        label: "Final Score", 
        value: `${results.final_score} out of ${results.total_questions}`, 
        icon: TrophyIcon 
      },
      { 
        label: "Score Percentage", 
        value: formatPercentage(results.score_percentage), 
        icon: CheckIcon 
      },
      { 
        label: "Total Questions", 
        value: results.total_questions, 
        icon: InfoIcon 
      },
      { 
        label: "Attempts Used", 
        value: `${results.attempts_used} out of ${maxAttempts || "Unlimited"}`, 
        icon: InfoIcon 
      },
      { 
        label: "Started At", 
        value: results.started_at !== "N/A" 
          ? new Date(results.started_at).toLocaleString("en-US", { 
              hour: "numeric", 
              minute: "numeric", 
              hour12: true, 
              day: "2-digit", 
              month: "short", 
              year: "numeric" 
            }) 
          : "N/A", 
        icon: TimeIcon 
      },
      { 
        label: "Ended At", 
        value: results.ended_at !== "N/A" 
          ? new Date(results.ended_at).toLocaleString("en-US", { 
              hour: "numeric", 
              minute: "numeric", 
              hour12: true, 
              day: "2-digit", 
              month: "short", 
              year: "numeric" 
            }) 
          : "N/A", 
        icon: TimeIcon 
      },
    ]
  : [];

  const itemsPerPage = 6;
  const totalPages = Math.ceil(resultFields.length / itemsPerPage);
  const paginatedFields = resultFields.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);



  // Enhanced `renderQuestion` function
  // Drop-in replacement for your existing function. It keeps the same handlers / variables
  // (e.g. userAnswers, questionStatuses, handleAnswerChange, handleNextQuestion, etc.) so
  // you can paste it into your codebase without changing component scope.

  const renderQuestion = (question, index) => {
    if (!question) {
      console.log("No question provided for index:", index);
      return null;
    }

    const isAnswered = (() => {
      const userAnswer = userAnswers[question.id];
      if (!userAnswer) return false;
      if (question.type === "image") {
        return question.labels?.every((label) => userAnswer[label.id]?.trim());
      }
      if (Array.isArray(userAnswer)) return userAnswer.length > 0;
      if (typeof userAnswer === "string") return userAnswer.trim() !== "";
      if (typeof userAnswer === "object") return Object.keys(userAnswer).length > 0;
      return false;
    })();

    const isSkipped = questionStatuses[index] === "skipped";
    const isLastQuestion = currentQuestionIndex === questions.length - 1;

    // Disable Next button if the question is skipped and not answered
    const shouldDisableNextButton = !isAnswered && isSkipped && !isLastQuestion;

    // Enhanced gradient colors
    const coreGradient = "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)";
    const secondaryGradient = "linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)";
    const skipGradient = "linear-gradient(135deg, #ff9800 0%, #ffb74d 100%)";
    const accentGlow = mode === "dark"
      ? "0 0 40px rgba(30, 139, 175, 0.15)"
      : "0 0 40px rgba(30, 58, 138, 0.1)";

    // Enhanced Paper/card base style with game-like aesthetics
    const paperStyles = {
      p: { xs: 2, sm: 3, md: 4 },
      mb: 2,
      mt: 2,
      width: "100%",
      minHeight: "90vh",
      maxWidth: 1200,
      mx: "auto",
      boxShadow: mode === "dark"
        ? "0 20px 60px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(30, 139, 175, 0.2)"
        : "0 20px 60px rgba(16, 24, 40, 0.12), 0 0 0 1px rgba(30, 58, 138, 0.08)",
      borderRadius: 4,
      background: mode === "dark"
        ? "linear-gradient(180deg, #1f2937 0%, #111827 100%)"
        : "linear-gradient(180deg, #ffffff 0%, #f9fafb 100%)",
      border: mode === "dark"
        ? "1px solid rgba(30, 139, 175, 0.15)"
        : "1px solid rgba(30, 58, 138, 0.1)",
      display: "flex",
      flexDirection: "column",
      position: "relative",
      overflow: "hidden",
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '4px',
        background: coreGradient,
        boxShadow: accentGlow
      }
    };

    // Enhanced header area with game-like design
    const headerStyles = {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 2,
      pb: 3,
      borderBottom: mode === "dark"
        ? "2px solid rgba(30, 139, 175, 0.2)"
        : "2px solid rgba(30, 58, 138, 0.15)",
      mb: 3,
      position: 'relative'
    };

    // Floating footer with glass morphism effect
    const stickyFooter = {
      position: "sticky",
      bottom: 0,
      left: 0,
      right: 0,
      // bgcolor: mode === "dark" 
      //   ? "rgba(31, 41, 55, 0.98)" 
      //   : "rgba(255, 255, 255, 0.98)",
      // backdropFilter: "blur(20px)",
      borderTop: mode === "dark"
        ? "2px solid rgba(30, 139, 175, 0.2)"
        : "2px solid rgba(30, 58, 138, 0.15)",
      py: 2.5,
      px: { xs: 2, sm: 3 },
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      mt: 3,
      // boxShadow: mode === "dark"
      //   ? "0 -10px 30px rgba(0, 0, 0, 0.3)"
      //   : "0 -10px 30px rgba(16, 24, 40, 0.08)"
    };

    // Enhanced button shared styles
    const actionBtnSx = {
      borderRadius: 2.5,
      px: 3.5,
      py: 1.25,
      textTransform: "none",
      fontWeight: 600,
      fontSize: "0.95rem",
      boxShadow: "none",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      minWidth: 100,
      position: 'relative',
      overflow: 'hidden',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: '-100%',
        width: '100%',
        height: '100%',
        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
        transition: 'left 0.5s ease'
      },
      '&:hover::before': {
        left: '100%'
      }
    };

    // Enhanced option card design with game-like interactivity
    const optionPaperSx = (selected) => ({
      p: 2,
      borderRadius: 2.5,
      cursor: "pointer",
      userSelect: "none",
      display: "flex",
      alignItems: "center",
      gap: 2,
      minHeight: 64,
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      // transform: selected ? "translateY(-4px) scale(1.02)" : "translateY(0) scale(1)",
      boxShadow: selected
        ? mode === "dark"
          ? "0 8px 24px rgba(30, 139, 175, 0.4), 0 0 0 2px rgba(30, 139, 175, 0.5)"
          : "0 8px 24px rgba(30, 139, 175, 0.3), 0 0 0 2px rgba(30, 58, 138, 0.3)"
        : mode === "dark"
          ? "0 2px 8px rgba(0, 0, 0, 0.2)"
          : "0 2px 8px rgba(16, 24, 40, 0.08)",
      border: selected
        ? `2px solid ${mode === "dark" ? "#1e8bafff" : "#1e3a8a"}`
        : mode === "dark"
          ? "2px solid rgba(255, 255, 255, 0.08)"
          : "2px solid rgba(16, 24, 40, 0.08)",
      backgroundColor: selected
        ? mode === "dark"
          ? "rgba(30, 139, 175, 0.15)"
          : "rgba(30, 139, 175, 0.08)"
        : mode === "dark"
          ? "rgba(255, 255, 255, 0.03)"
          : "rgba(255, 255, 255, 1)",
      position: 'relative',
      overflow: 'hidden',
      '&::before': selected ? {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: mode === "dark"
          ? "linear-gradient(135deg, rgba(30, 139, 175, 0.1) 0%, rgba(30, 58, 138, 0.05) 100%)"
          : "linear-gradient(135deg, rgba(30, 139, 175, 0.05) 0%, rgba(30, 58, 138, 0.02) 100%)",
        pointerEvents: 'none'
      } : {},
      '&:hover': {
        boxShadow: mode === "dark"
          ? "0 8px 24px rgba(30, 139, 175, 0.3), 0 0 0 2px rgba(30, 139, 175, 0.3)"
          : "0 8px 24px rgba(30, 139, 175, 0.2), 0 0 0 2px rgba(30, 58, 138, 0.2)",
        // transform: 'translateY(-4px) scale(1.02)',
        borderColor: mode === "dark" ? "#1e8bafff" : "#1e3a8a"
      },
      '&:active': {
        // transform: 'translateY(-2px) scale(0.98)'
      }
    });

    // Button container with improved layout
    const buttonContainer = (
      <Box sx={stickyFooter}>
        <Box sx={{ display: "flex", gap: 1.5 }}>
          <Button
            variant="contained"
            onClick={handleSkipQuestion}
            sx={{
              ...actionBtnSx,
              background: skipGradient,
              color: "#fff",
              '&:hover': {
                background: "linear-gradient(135deg, #e68a00 0%, #ffa726 100%)",
                boxShadow: "0 6px 16px rgba(255, 152, 0, 0.4)",
                transform: 'translateY(-2px)'
              },
            }}
          >
            Skip
          </Button>
          <Button
            variant="outlined"
            onClick={handleReviewOpen}
            sx={{
              ...actionBtnSx,
              color: mode === "dark" ? "#e6eefc" : "#1e3a8a",
              borderColor: mode === "dark" ? "rgba(230, 238, 252, 0.3)" : "rgba(30, 58, 138, 0.3)",
              borderWidth: '2px',
              '&:hover': {
                borderColor: mode === "dark" ? "#e6eefc" : "#1e3a8a",
                backgroundColor: mode === "dark" ? "rgba(230, 238, 252, 0.1)" : "rgba(30, 58, 138, 0.05)",
                borderWidth: '2px',
                transform: 'translateY(-2px)',
                boxShadow: mode === "dark"
                  ? "0 6px 16px rgba(230, 238, 252, 0.2)"
                  : "0 6px 16px rgba(30, 58, 138, 0.15)"
              },
            }}
          >
            Review
          </Button>
        </Box>

        <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
          {/* Previous Button */}
          <Button
            variant="contained"
            onClick={handlePreviousQuestion}
            disabled={currentQuestionIndex === 0}
            startIcon={<ArrowBackIcon />}
            sx={{
              ...actionBtnSx,
              background: mode === "dark"
                ? "linear-gradient(135deg, #4a5568 0%, #2d3748 100%)"
                : "linear-gradient(135deg, #e2e8f0 0%, #cbd5e0 100%)",
              color: mode === "dark" ? "#e6eefc" : "#1e3a8a",
              minWidth: 100,
              '&:hover': {
                background: mode === "dark"
                  ? "linear-gradient(135deg, #5a6a7d 0%, #3a485d 100%)"
                  : "linear-gradient(135deg, #d1d9e6 0%, #b8c5d6 100%)",
                boxShadow: mode === "dark"
                  ? "0 6px 16px rgba(74, 85, 104, 0.4)"
                  : "0 6px 16px rgba(226, 232, 240, 0.4)",
                transform: 'translateY(-2px)'
              },
              '&:disabled': {
                background: mode === "dark"
                  ? "rgba(74, 85, 104, 0.3)"
                  : "rgba(226, 232, 240, 0.3)",
                color: mode === "dark"
                  ? "rgba(230, 238, 252, 0.4)"
                  : "rgba(30, 58, 138, 0.4)"
              }
            }}
            aria-label="previous question"
          >
            Prev
          </Button>

          {/* Next Button */}
          <Tooltip
            title={
              isLastQuestion
                ? ""
                : !(isAnswered || isSkipped)
                  ? "Please answer or skip the question to proceed."
                  : ""
            }
            placement="top"
            arrow
          >
            <Button
              variant="contained"
              onClick={handleNextQuestion}
              disabled={isLastQuestion || shouldDisableNextButton}
              endIcon={<ArrowForwardIcon />}
              sx={{
                ...actionBtnSx,
                background: coreGradient,
                color: "#fff",
                minWidth: 100,
                "&:hover": {
                  background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
                  opacity: 0.9,
                  boxShadow: "0 8px 20px rgba(30, 58, 138, 0.5)",
                  transform: "translateY(-2px)",
                },
                "&:disabled": {
                  background: "#9e9e9e",
                  color: "#fff",
                },
              }}
              aria-label="next question"
            >
              {isLastQuestion ? "Finish" : "Next"}
            </Button>
          </Tooltip>

          {/* Submit Button */}
          <Button
            variant="contained"
            onClick={handleSubmitQuiz}
            disabled={isSubmitting}
            sx={{
              ...actionBtnSx,
              background: secondaryGradient,
              color: "#fff",
              minWidth: 120,
              '&:hover': {
                background: "linear-gradient(135deg, #388e3c 0%, #58a552 100%)",
                boxShadow: "0 8px 20px rgba(76, 175, 80, 0.4)",
                transform: 'translateY(-2px)'
              },
              '&:disabled': {
                background: "#9e9e9e",
                color: "#fff"
              }
            }}
          >
            {isSubmitting ? "Submitting..." : "Submit Quiz"}
          </Button>
        </Box>
      </Box>
    );

    // Progress indicator
    const progress = Math.min(Math.max(currentQuestionIndex + 1, 0), questions.length) / Math.max(questions.length, 1);

    // Enhanced top area for all question types
    const TopMeta = () => (
      <Box sx={headerStyles}>
        <Box sx={{ display: 'flex', gap: 2.5, alignItems: 'center', flex: 1 }}>
          <Box sx={{
            width: 6,
            height: 48,
            borderRadius: 3,
            background: coreGradient,
            boxShadow: accentGlow
          }} />
          <Box sx={{ flex: 1 }}>
            <Box sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1.5,
              px: 2.5,
              py: 1,
              borderRadius: 2.5,
              background: mode === "dark"
                ? "linear-gradient(135deg, rgba(30, 139, 175, 0.15) 0%, rgba(30, 58, 138, 0.1) 100%)"
                : "linear-gradient(135deg, rgba(30, 139, 175, 0.1) 0%, rgba(30, 58, 138, 0.08) 100%)",
              border: mode === "dark"
                ? "1px solid rgba(30, 139, 175, 0.3)"
                : "1px solid rgba(30, 58, 138, 0.2)",
              mb: 2,
              boxShadow: mode === "dark"
                ? "0 2px 8px rgba(30, 139, 175, 0.2)"
                : "0 2px 8px rgba(30, 58, 138, 0.1)"
            }}>
              <Typography variant="caption" sx={{
                color: mode === "dark" ? "#e6eefc" : "#1e3a8a",
                fontWeight: 700,
                letterSpacing: "0.8px",
                fontSize: "0.8rem",
                textTransform: "uppercase"
              }}>
                {question.type === 'mcq' ? '◉ Multiple Choice' :
                  question.type === 'tf' ? '✓ True / False' :
                    question.type === 'image' ? '🖼 Image Question' :
                      question.type === 'sub' ? '✍ Subjective' :
                        question.type === 'fib' ? '__ Fill in Blanks' : '● Question'}
              </Typography>
            </Box>

            <Typography variant="h6" sx={{
              mt: 0.5,
              fontWeight: 700,
              fontSize: { xs: '1.1rem', sm: '1.25rem' },
              lineHeight: 1.4,
              color: mode === "dark" ? "#fff" : "#1f2937"
            }}>
              {index + 1}. {
                renderContent(
                  question.type === 'fib'
                    ? (question.statement?.replace(/\[BLANK\]/g, '_______') || 'No statement available')
                    : (question.statement || 'No statement available')
                )
              }
            </Typography>
            {question.tags && question.tags.length > 0 && (
              <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                {question.tags.map((tag, i) => (
                  <Chip
                    key={i}
                    label={`#${tag.name}`}
                    size="small"
                    sx={{
                      backgroundColor: mode === "dark"
                        ? "rgba(30, 139, 175, 0.15)"
                        : "rgba(30, 58, 138, 0.1)",
                      color: mode === "dark" ? "#e6eefc" : "#1e3a8a",
                      fontWeight: 600,
                      borderRadius: 2,
                      fontSize: '0.75rem',
                      border: mode === "dark"
                        ? "1px solid rgba(30, 139, 175, 0.3)"
                        : "1px solid rgba(30, 58, 138, 0.2)",
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: mode === "dark"
                          ? "rgba(30, 139, 175, 0.25)"
                          : "rgba(30, 58, 138, 0.15)",
                        transform: 'translateY(-2px)'
                      }
                    }}
                  />
                ))}
              </Box>
            )}
          </Box>
        </Box>
        <Box sx={{ width: { xs: 180, sm: 240 }, textAlign: 'right' }}>
          {isTimerEnabled && (
            <Box sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              px: 2.5,
              py: 1.25,
              borderRadius: 2.5,
              background: totalTimeLeft <= 10
                ? 'linear-gradient(135deg, #ff5252 0%, #ff1744 100%)'
                : mode === 'dark'
                  ? 'linear-gradient(135deg, rgba(30, 139, 175, 0.15) 0%, rgba(30, 58, 138, 0.1) 100%)'
                  : 'linear-gradient(135deg, rgba(30, 139, 175, 0.1) 0%, rgba(30, 58, 138, 0.08) 100%)',
              border: totalTimeLeft <= 10
                ? '2px solid #ff1744'
                : mode === 'dark'
                  ? '1px solid rgba(30, 139, 175, 0.3)'
                  : '1px solid rgba(30, 58, 138, 0.2)',
              boxShadow: totalTimeLeft <= 10
                ? '0 4px 12px rgba(255, 23, 68, 0.3), 0 0 20px rgba(255, 23, 68, 0.2)'
                : mode === 'dark'
                  ? '0 2px 8px rgba(30, 139, 175, 0.2)'
                  : '0 2px 8px rgba(30, 58, 138, 0.1)',
              animation: totalTimeLeft <= 10 ? 'pulse 1s ease-in-out infinite' : 'none',
              mb: 1.5
            }}>
              <Typography variant="body2" sx={{
                color: totalTimeLeft <= 10 ? '#fff' : mode === 'dark' ? '#e6eefc' : '#1e3a8a',
                fontWeight: 700,
                fontSize: '0.95rem',
                fontFamily: 'monospace'
              }}>
                ⏱ {Math.floor(totalTimeLeft / 60)}:{(totalTimeLeft % 60).toString().padStart(2, '0')}
              </Typography>
            </Box>
          )}
          <Typography variant="caption" sx={{
            display: 'block',
            mt: 1,
            color: mode === "dark" ? "rgba(255, 255, 255, 0.6)" : "rgba(31, 41, 55, 0.6)",
            fontWeight: 600,
            fontSize: '0.8rem'
          }}>
            Question {index + 1} of {questions.length}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={progress * 100}
            sx={{
              height: 8,
              borderRadius: 4,
              mt: 1.5,
              backgroundColor: mode === "dark"
                ? "rgba(255, 255, 255, 0.08)"
                : "rgba(30, 58, 138, 0.1)",
              boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.1)',
              '& .MuiLinearProgress-bar': {
                background: coreGradient,
                boxShadow: accentGlow,
                borderRadius: 4,
                transition: 'transform 0.4s ease'
              }
            }}
          />
        </Box>
      </Box>
    );

    // Question type cases with enhanced styling
    switch (question.type) {
      case "image": {
        const currentAnswers = userAnswers[question.id] || {};
        return (
          <Paper className={`quiz-card ${mode}-mode`} key={question.id} sx={paperStyles}>
            <TopMeta />
            <Box sx={{
              display: 'flex',
              gap: 3,
              mt: 2,
              flexDirection: { xs: 'column', md: 'row' },
              flexGrow: 1
            }}>
              <Box sx={{
                flex: 1,
                overflow: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: 2
              }}>
                <ImageQuestionDisplay
                  question={question}
                  userAnswers={currentAnswers}
                  onAnswerChange={handleAnswerChange}
                   mode={mode} 
                />
              </Box>
              {question.image && (
                <Box sx={{
                  width: { xs: '100%', md: '40%' },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 2,
                  borderRadius: 3,
                  background: mode === "dark"
                    ? 'rgba(255, 255, 255, 0.02)'
                    : 'rgba(30, 58, 138, 0.02)',
                  border: mode === "dark"
                    ? '2px solid rgba(30, 139, 175, 0.2)'
                    : '2px solid rgba(30, 58, 138, 0.1)'
                }}>
                  <img
                    src={typeof question.image === 'string' ? `http://warals1.ddns.net:9017${question.image}` : URL.createObjectURL(question.image)}
                    alt="Question"
                    style={{
                      maxWidth: '100%',
                      maxHeight: '48vh',
                      objectFit: 'contain',
                      borderRadius: 12,
                      border: mode === "dark" ? '2px solid rgba(30, 139, 175, 0.3)' : '2px solid rgba(30, 58, 138, 0.2)',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      boxShadow: mode === "dark"
                        ? '0 8px 24px rgba(0, 0, 0, 0.3)'
                        : '0 8px 24px rgba(16, 24, 40, 0.1)'
                    }}
                    onClick={() => {
                      const imgUrl = typeof question.image === 'string' ? `http://warals1.ddns.net:9017${question.image}` : URL.createObjectURL(question.image);
                      window.open(imgUrl, '_blank');
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'scale(1.05)';
                      e.target.style.boxShadow = mode === "dark"
                        ? '0 12px 32px rgba(30, 139, 175, 0.4)'
                        : '0 12px 32px rgba(30, 58, 138, 0.2)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'scale(1)';
                      e.target.style.boxShadow = mode === "dark"
                        ? '0 8px 24px rgba(0, 0, 0, 0.3)'
                        : '0 8px 24px rgba(16, 24, 40, 0.1)';
                    }}
                  />
                </Box>
              )}
            </Box>
            {buttonContainer}
          </Paper>
        );
      }

      case "mcq": {
        const mcqOptions = question.options || [];
        const selectedAnswers = userAnswers[question.id] || [];
        return (
          <Paper className={`quiz-card ${mode}-mode`} key={question.id} sx={paperStyles}>
            <TopMeta />
            {question.image && (
              <Box sx={{
                my: 3,
                textAlign: 'center',
                p: 2,
                borderRadius: 3,
                background: mode === "dark"
                  ? 'rgba(255, 255, 255, 0.02)'
                  : 'rgba(30, 58, 138, 0.02)'
              }}>
                <img
                  src={typeof question.image === 'string' ? `http://warals1.ddns.net:9017${question.image}` : URL.createObjectURL(question.image)}
                  alt="MCQ"
                  style={{
                    maxWidth: '40%',
                    borderRadius: 12,
                    cursor: 'pointer',
                    border: mode === "dark" ? '2px solid rgba(30, 139, 175, 0.3)' : '2px solid rgba(30, 58, 138, 0.2)',
                    transition: 'all 0.3s ease',
                    boxShadow: mode === "dark"
                      ? "0 8px 24px rgba(0, 0, 0, 0.3)"
                      : "0 8px 24px rgba(16, 24, 40, 0.1)"
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'scale(1.05)';
                    e.target.style.boxShadow = mode === "dark"
                      ? '0 12px 32px rgba(30, 139, 175, 0.4)'
                      : '0 12px 32px rgba(30, 58, 138, 0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'scale(1)';
                    e.target.style.boxShadow = mode === "dark"
                      ? '0 8px 24px rgba(0, 0, 0, 0.3)'
                      : '0 8px 24px rgba(16, 24, 40, 0.1)';
                  }}
                  onClick={() => {
                    const imgUrl = typeof question.image === 'string' ? `http://warals1.ddns.net:9017${question.image}` : URL.createObjectURL(question.image);
                    window.open(imgUrl, '_blank');
                  }}
                />
              </Box>
            )}
            <Box sx={{ flexGrow: 1, overflow: 'auto', mt: 1 }}>
              {mcqOptions.length > 0 ? (
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  {mcqOptions.map((option, i) => {
                    const optionText = option.answer_text;
                    const selected = Array.isArray(selectedAnswers) && selectedAnswers.includes(optionText);
                    return (
                      <Grid item xs={12} sm={6} key={option.id || i}>
                        <Paper
  elevation={0}
  sx={optionPaperSx(selected)}
  onClick={(e) => {
    e.stopPropagation(); // Prevent event bubbling
    handleAnswerChange(question.id, optionText, true);
  }}
>
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
    <Checkbox
      checked={selected}
      onChange={(e) => {
        e.stopPropagation(); // Prevent event bubbling
        handleAnswerChange(question.id, optionText, true);
      }}
      onClick={(e) => e.stopPropagation()} // Prevent Paper's onClick from firing
      sx={{ '&.Mui-checked': { color: mode === "dark" ? "#1e8bafff" : "#1e3a8a" }, padding: 1 }}
    />
    <Box sx={{ flexGrow: 1, fontWeight: 500 }}>{renderContent(optionText)}</Box>
  </Box>
</Paper>

                      </Grid>
                    );
                  })}
                </Grid>
              ) : (
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                  No options available for this question.
                </Typography>
              )}
            </Box>
            {buttonContainer}
          </Paper>
        );
      }

      case "tf": {
        const tfOptions = question.options || [];
        return (
          <Paper className={`quiz-card ${mode}-mode`} key={question.id} sx={paperStyles}>
            <TopMeta />
            <Box sx={{ flexGrow: 1, overflow: 'auto', mt: 1 }}>
              {tfOptions.length > 0 ? (
                <FormControl component="fieldset" sx={{ width: '100%' }}>
                  <RadioGroup value={userAnswers[question.id] || ""} onChange={(e) => handleAnswerChange(question.id, e.target.value)}>
                    {tfOptions.map((option, i) => {
                      const selected = userAnswers[question.id] === option.answer_text;
                      return (
                       <Paper
  key={option.id || i}
  elevation={0}
  sx={{ ...optionPaperSx(selected), mb: 2, cursor: 'pointer' }}
  onClick={(e) => {
    e.stopPropagation(); // Prevent event bubbling
    handleAnswerChange(question.id, option.answer_text);
  }}
>
  <FormControlLabel
    value={option.answer_text}
    control={
      <Radio
        sx={{ '&.Mui-checked': { color: mode === "dark" ? "#1e8bafff" : "#1e3a8a" }, padding: 1 }}
        onClick={(e) => e.stopPropagation()} // Prevent Paper's onClick from firing
      />
    }
    label={renderContent(option.answer_text)}
    sx={{ width: '100%', margin: 0, '& .MuiFormControlLabel-label': { fontWeight: 500, flexGrow: 1 } }}
    onClick={(e) => e.stopPropagation()} // Prevent double firing
  />
</Paper>

                      );
                    })}
                  </RadioGroup>
                </FormControl>
              ) : (
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                  No options available for this question.
                </Typography>
              )}
            </Box>
            {buttonContainer}
          </Paper>
        );
      }

      case "fib": {
  const fibOptions = question.rawAnswers && question.rawAnswers.length > 0
    ? question.rawAnswers.map((ans) => ans.answer)
    : [];
  const selectedAnswers = Array.isArray(userAnswers[question.id])
    ? userAnswers[question.id]
    : [];

  return (
    <Paper className={`quiz-card ${mode}-mode`} key={question.id} sx={paperStyles}>
      <TopMeta />
      <Box sx={{ flexGrow: 1, overflow: 'auto', mt: 1 }}>
        {fibOptions.length > 0 ? (
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {fibOptions.map((option, i) => {
              const selected = selectedAnswers.includes(option);
              return (
                <Grid item xs={12} sm={6} key={i}>
                  <Paper
                    elevation={0}
                    sx={optionPaperSx(selected)}
                    onClick={() => {}}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                      <Checkbox
                        checked={selected}
                        onChange={() => handleAnswerChange(question.id, option, true)}
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          '&.Mui-checked': {
                            color: mode === "dark" ? "#1e8bafff" : "#1e3a8a"
                          },
                          padding: 1
                        }}
                      />
                      <Box
                        sx={{ flexGrow: 1, fontWeight: 500, cursor: 'pointer' }}
                        onClick={() => handleAnswerChange(question.id, option, true)}
                      >
                        {renderContent(option)}
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        ) : (
          <Typography variant="body2" color="error">
            No options available
          </Typography>
        )}
      </Box>
      {buttonContainer}
    </Paper>
  );
}


      case "sub": {
        return (
          <Paper className={`quiz-card ${mode}-mode`} key={question.id} sx={paperStyles}>
            <TopMeta />
            <Box sx={{ flexGrow: 1, overflow: 'auto', mt: 1 }}>
              <Box sx={{
                p: 2,
                borderRadius: 2.5,
                background: mode === "dark"
                  ? 'rgba(255, 255, 255, 0.02)'
                  : 'rgba(30, 58, 138, 0.02)',
                border: mode === "dark"
                  ? '2px solid rgba(30, 139, 175, 0.2)'
                  : '2px solid rgba(30, 58, 138, 0.1)',
                boxShadow: mode === "dark"
                  ? '0 2px 8px rgba(0, 0, 0, 0.2)'
                  : '0 2px 8px rgba(16, 24, 40, 0.08)'
              }}>
                <ReactQuill
                  value={userAnswers[question.id] || ""}
                  onChange={(value) => handleAnswerChange(question.id, value)}
                  placeholder="Enter your answer here..."
                  modules={{
                    toolbar: [
                      // ["bold", "italic", "underline", "strike"],
                      [{ list: "ordered" }, { list: "bullet" }],
                      [{ script: "sub" }, { script: "super" }],
                      ["clean"],
                    ],
                  }}
                  formats={[
                    "list", "bullet", "background", "script"]}
                  style={{
                    backgroundColor: mode === "dark" ? "#0f1720" : "#fff",
                    borderRadius: "8px",
                    minHeight: 220,
                    border: mode === "dark" ? "1px solid rgba(30, 139, 175, 0.2)" : "1px solid rgba(30, 58, 138, 0.1)"
                  }}
                />
              </Box>
            </Box>
            {buttonContainer}
          </Paper>
        );
      }

      default:
        console.log("Unknown question type:", question.type);
        return null;
    }
  };


  // End of enhanced function

  const renderReviewQuestion = (question, index) => {
    const userAnswer = tempAnswers[question.id];
    if (!question) return null;

    const displayUserAnswer = (answer) => {
      if (!answer) return "Not answered";
      if (typeof answer === "object" && !Array.isArray(answer)) {
        const allLabelsAnswered = Object.values(answer).every((val) => val?.trim());
        if (!allLabelsAnswered) return "Not answered";

        return Object.entries(answer).map(([labelId, labelAnswer]) => (
          <Typography key={labelId} variant="body2" sx={{ color: "textSecondary", mb: 0.5 }}>
            <strong>Label {labelId}:</strong> {renderContent(labelAnswer)}
          </Typography>
        ));
      }
      if (Array.isArray(answer)) {
        return answer.map((ans, i) => (
          <Typography key={i} variant="body2" sx={{ color: "textSecondary", mb: 0.5 }}>
            {renderContent(ans)}
          </Typography>
        ));
      }
      return renderContent(answer);
    };

    switch (question.type) {
      case "image":
        return (
          <Box key={question.id} sx={{ mb: 3 }}>
            <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
              {index + 1}: {renderContent(question.statement || "No statement")}
            </Typography>
            <Typography variant="body2" sx={{ color: "textSecondary", mb: 1 }}>
              <strong>Your Answers:</strong>
            </Typography>
            {userAnswer && typeof userAnswer === "object" && !Array.isArray(userAnswer) ? (
              Object.values(userAnswer).some((val) => val?.trim()) ? (
                Object.entries(userAnswer).map(([labelId, labelAnswer]) => (
                  <Typography key={labelId} variant="body2" sx={{ color: "textSecondary", mb: 0.5 }}>
                    <strong>Label {labelId}:</strong> {renderContent(labelAnswer)}
                  </Typography>


                ))
              ) : (
                <Typography variant="body2" sx={{ color: "textSecondary" }}>
                  Not answered
                </Typography>
              )
            ) : (
              <Typography variant="body2" sx={{ color: "textSecondary" }}>
                Not answered
              </Typography>
            )}


          </Box>
        );
      case "mcq":
        return (
          <Box key={question.id} sx={{ mb: 3 }}>
            <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
              {index + 1}: {renderContent(question.statement || "No statement")}
            </Typography>
            <Typography variant="body2" sx={{ color: "textSecondary", mb: 1 }}>
              <strong>Your Answer:</strong>
            </Typography>
            {userAnswer && Array.isArray(userAnswer) ? (
              userAnswer.map((ans, i) => (
                <Typography key={i} variant="body2" sx={{ color: "textSecondary", mb: 0.5 }}>
                  {renderContent(ans)}
                </Typography>
              ))
            ) : (
              <Typography variant="body2" sx={{ color: "textSecondary" }}>
                Not answered
              </Typography>
            )}
            <Typography variant="body2" sx={{ color: "textSecondary", mt: 1 }}>
              <strong>Options:</strong>
            </Typography>
            <FormGroup>
              {question.options.map((option, i) => (
              <FormControlLabel
  control={
    <Checkbox
      checked={userAnswer?.includes(option.answer_text)}
      disabled
      onClick={(e) => e.stopPropagation()} // Prevent event bubbling
    />
  }
  label={renderContent(option.answer_text)}
  onClick={(e) => e.stopPropagation()} // Prevent event bubbling
/>
              ))}
            </FormGroup>
          </Box>
        );
      case "tf":
        return (
          <Box key={question.id} sx={{ mb: 3 }}>
            <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
              {index + 1}: {renderContent(question.statement || "No statement")}
            </Typography>
            <Typography variant="body2" sx={{ color: "textSecondary", mb: 1 }}>
              <strong>Your Answer:</strong> {userAnswer ? renderContent(userAnswer) : "Not answered"}
            </Typography>
            <RadioGroup value={userAnswer || ""}>
              {question.options.map((option, i) => (
                <FormControlLabel
                  key={i}
                  value={option.answer_text}
                  control={<Radio disabled />}
                  label={renderContent(option.answer_text)}
                />
              ))}
            </RadioGroup>
          </Box>
        );
      case "fib":
        return (
          <Box key={question.id} sx={{ mb: 3 }}>
            <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
              {index + 1}: {renderContent(question.statement?.replace(/\[BLANK\]/g, "_____") || "No statement")}
            </Typography>
            <Typography variant="body2" sx={{ color: "textSecondary" }}>
              <strong>Your Answer:</strong> {displayUserAnswer(userAnswer)}
            </Typography>
          </Box>
        );
      case "sub":
        return (
          <Box key={question.id} sx={{ mb: 3 }}>
            <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
              {index + 1}: {renderContent(question.statement || "No statement")}
            </Typography>
            <Typography variant="body2" sx={{ color: "textSecondary" }}>
              <strong>Your Answer:</strong> {displayUserAnswer(userAnswer)}
            </Typography>
          </Box>
        );
      default:
        return <Typography>Unsupported question type: {question.type}</Typography>;
    }
  };



  return (
    <Box
      className={`quiz-modal ${mode}-mode`}
      sx={{ padding: 3, minHeight: "100vh", position: "relative", background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)", }}
    >
      <Box
        sx={{
          position: "absolute",
          top: 16,
          right: 16,
          display: "flex",
          gap: 1,
          zIndex: 10000,
        }}
      >
        {/* Info Button: visible only during quiz practice */}
        {questions.length > 0 && !results && !isStartModalOpen && (

          <IconButton
            onClick={() => setQuestionListOpen(true)}
            sx={{
              color: "#0288D1",
              bgcolor: "#E1F5FE",
              "&:hover": {
                bgcolor:
                  mode === "dark"
                    ? "#71c4ebff"
                    : mode === "custom"
                      ? customColors.background
                      : "#2880a8ff",
              },
              borderRadius: "50%",
              boxShadow: 2,
            }}
          >
            <InfoIcon />
          </IconButton>
        )}

        {/* Close Button: always visible, behavior depends on quiz state */}
        <IconButton
          onClick={handleCancelQuiz}
          sx={{
            color: "#D32F2F",
            bgcolor: "#FFEBEE",
            "&:hover": { bgcolor: "#EF9A9A" },
            borderRadius: "50%",
            boxShadow: 2,
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>
      {/* Only show Start Quiz modal when isStartModalOpen and shouldShowStartModal are true */}
      {isStartModalOpen && shouldShowStartModal ? (
        <Modal
          open={isStartModalOpen && shouldShowStartModal}
          aria-labelledby="start-quiz-modal"
          aria-describedby="start-quiz-settings"
          sx={{ zIndex: 9999 }}
        >
          <Fade in={isStartModalOpen && shouldShowStartModal}>
            <Box
              className={`quiz-card ${mode}-mode`}
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: { xs: "90%", sm: 500 },
                bgcolor: "white",
                borderRadius: "16px",
                boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
                p: { xs: 3, sm: 4 },
                animation: "scaleIn 0.3s ease-out",
                "@keyframes scaleIn": {
                  "0%": {
                    transform: "translate(-50%, -50%) scale(0.8)",
                    opacity: 0,
                  },
                  "100%": {
                    transform: "translate(-50%, -50%) scale(1)",
                    opacity: 1,
                  },
                },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", mb: 3, justifyContent: "center" }}>
                <CheckIcon
                  sx={{
                    fontSize: 40,
                    color: mode === "dark" ? "#95bdebff" : mode === "custom" ? customColors.text : "#0277bd",
                    mr: 1,
                  }}
                />
                <Typography
                  id="start-quiz-modal"
                  variant="h5"
                  component="h2"
                  sx={{
                    fontWeight: 700,
                    background: mode === "dark"
                      ? "linear-gradient(45deg, #0277bd, #01579b)"
                      : mode === "custom"
                        ? customColors.text
                        : "linear-gradient(45deg, #0277bd, #01579b)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  Quiz Settings
                </Typography>
              </Box>
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <TextField
                    fullWidth
                    label="Max Attempts"
                    type="number"
                    value={maxAttempts}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === "" || (Number(value) >= 1 && Number.isInteger(Number(value)))) {
                        setMaxAttempts(value);
                      }
                    }}
                    InputLabelProps={{
                      style: {
                        color:
                          mode === "dark"
                            ? "#ffffff"
                            : mode === "custom"
                              ? customColors.text
                              : "#000000",
                      },
                    }}
                    placeholder="Leave blank for unlimited"
                    variant="outlined"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        "& fieldset": { borderColor: "#b0bec5" },
                        "&:hover fieldset": { borderColor: "#0277bd" },
                        "&.Mui-focused fieldset": { borderColor: "#0277bd" },
                      },
                    }}
                  />
                  <Tooltip
                    title="Set the maximum number of attempts allowed for the quiz. Leave blank for unlimited attempts."
                    placement="right"
                    arrow
                    PopperProps={{ style: { zIndex: 10000 } }}
                    sx={{
                      "& .MuiTooltip-tooltip": {
                        fontSize: "0.9rem",
                        bgcolor: "#0277bd",
                        color: "white",
                        borderRadius: "8px",
                        p: 1.5,
                      },
                      "& .MuiTooltip-arrow": { color: "#0277bd" },
                    }}
                  >
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke={mode === "dark" ? "#0277bd" : mode === "custom" ? customColors.text : "#0277bd"}
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{ marginLeft: "8px", cursor: "pointer" }}
                    >
                      <circle cx="12" cy="12" r="10" />
                      <path d="M12 16v.01" />
                      <path d="M12 8v4" />
                    </svg>
                  </Tooltip>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <TextField
                    fullWidth
                    label="Passing Percentage"
                    type="number"
                    value={passingPercentage}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === "" || (Number(value) >= 1 && Number(value) <= 100)) {
                        setPassingPercentage(value);
                      }
                    }}
                    InputLabelProps={{
                      style: {
                        color:
                          mode === "dark"
                            ? "#ffffff"
                            : mode === "custom"
                              ? customColors.text
                              : "#000000",
                      },
                    }}
                    placeholder="Enter percentage (defaults to 35)"
                    variant="outlined"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        "& fieldset": { borderColor: "#b0bec5" },
                        "&:hover fieldset": { borderColor: "#0277bd" },
                        "&.Mui-focused fieldset": { borderColor: "#0277bd" },
                      },
                    }}
                  />
                  <Tooltip
                    title="Set the minimum percentage required to pass the quiz (1-100). Defaults to 35% if not specified."
                    placement="right"
                    arrow
                    PopperProps={{ style: { zIndex: 10000 } }}
                    sx={{
                      "& .MuiTooltip-tooltip": {
                        fontSize: "0.9rem",
                        bgcolor: "#0277bd",
                        color: "white",
                        borderRadius: "8px",
                        p: 1.5,
                      },
                      "& .MuiTooltip-arrow": { color: "#0277bd" },
                    }}
                  >
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke={mode === "dark" ? "#0277bd" : mode === "custom" ? customColors.text : "#0277bd"}
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{ marginLeft: "8px", cursor: "pointer" }}
                    >
                      <circle cx="12" cy="12" r="10" />
                      <path d="M12 16v.01" />
                      <path d="M12 8v4" />
                    </svg>
                  </Tooltip>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2, flexWrap: "wrap" }}>
                  <FormControl component="fieldset" sx={{ flex: "0 0 auto", mr: 4 }}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <FormLabel
                        component="legend"
                        sx={{ color: mode === "dark" ? "white" : mode === "custom" ? customColors.text : "#333", mb: 1 }}
                      >
                        Negative Marking
                      </FormLabel>
                      <Tooltip
                        title="Enable negative marking to deduct points for incorrect answers. If disabled, incorrect answers do not affect the score."
                        placement="left"
                        arrow
                        PopperProps={{ style: { zIndex: 10000 } }}
                        sx={{
                          "& .MuiTooltip-tooltip": {
                            fontSize: "0.9rem",
                            bgcolor: "#0277bd",
                            color: "white",
                            borderRadius: "8px",
                            p: 1.5,
                          },
                          "& .MuiTooltip-arrow": { color: "#0277bd" },
                        }}
                      >
                        <svg
                          width="17"
                          height="17"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke={mode === "dark" ? "#0277bd" : mode === "custom" ? customColors.text : "#0277bd"}
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          style={{ marginLeft: "8px", marginBottom: "4px", cursor: "pointer" }}
                        >
                          <circle cx="12" cy="12" r="10" />
                          <path d="M12 16v.01" />
                          <path d="M12 8v4" />
                        </svg>
                      </Tooltip>
                    </Box>
                    <RadioGroup
                      row
                      value={negativeMarking ? "yes" : "no"}
                      onChange={(e) => setNegativeMarking(e.target.value === "yes")}
                    >
                      <FormControlLabel value="yes" control={<Radio sx={{ color: mode === "dark" ? "#0277bd" : mode === "custom" ? customColors.text : "#0277bd" }} />} label="Yes" />
                      <FormControlLabel value="no" control={<Radio sx={{ color: mode === "dark" ? "#0277bd" : mode === "custom" ? customColors.text : "#0277bd" }} />} label="No" />
                    </RadioGroup>
                  </FormControl>
                  <FormControl component="fieldset" sx={{ flex: "0 0 auto", marginLeft: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <FormLabel
                        component="legend"
                        sx={{ color: mode === "dark" ? "white" : mode === "custom" ? customColors.text : "#333", mb: 1 }}
                      >
                        Enable Timer
                      </FormLabel>
                      <Tooltip
                        title="Enable the timer to set a time limit for the quiz (30 seconds per question). Disable to take the quiz without a time constraint."
                        placement="right"
                        arrow
                        PopperProps={{ style: { zIndex: 10000 } }}
                        sx={{
                          "& .MuiTooltip-tooltip": {
                            fontSize: "0.9rem",
                            bgcolor: "#0277bd",
                            color: "white",
                            borderRadius: "8px",
                            p: 1.5,
                          },
                          "& .MuiTooltip-arrow": { color: "#0277bd" },
                        }}
                      >
                        <svg
                          width="17"
                          height="17"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke={mode === "dark" ? "#0277bd" : mode === "custom" ? customColors.text : "#0277bd"}
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          style={{ marginLeft: "8px", marginBottom: "4px", cursor: "pointer" }}
                        >
                          <circle cx="12" cy="12" r="10" />
                          <path d="M12 16v.01" />
                          <path d="M12 8v4" />
                        </svg>
                      </Tooltip>
                    </Box>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={isTimerEnabled}
                          onChange={(e) => setIsTimerEnabled(e.target.checked)}
                          sx={{
                            "& .MuiSwitch-switchBase.Mui-checked": {
                              color: mode === "dark" ? "#0277bd" : mode === "custom" ? customColors.bgColor : "#0277bd",
                            },
                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                              backgroundColor: mode === "dark" ? "#0277bd" : mode === "custom" ? customColors.bgColor : "#0277bd",
                            },
                          }}
                        />
                      }
                      label={isTimerEnabled ? "Timer Enabled" : "Timer Disabled"}
                    />
                  </FormControl>
                </Box>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <Button
                  variant="contained"
                  sx={{
                    bgcolor: "#0277bd",
                    color: "white",
                    borderRadius: "50px",
                    px: 4,
                    py: 1.5,
                    fontWeight: 600,
                    "&:hover": { bgcolor: "#01579b", transform: "scale(1.05)", transition: "all 0.2s" },
                  }}
                  onClick={handleStartQuiz}
                >
                  Start Quiz
                </Button>
              </Box>
            </Box>
          </Fade>
        </Modal>
      ) : (
        <Box>
          {/* Show cancel button only after quiz starts */}

          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", mb: 4 }}>
              <CircularProgress color="primary" />
            </Box>
          ) : questions.length === 0 ? (
            <Box sx={{ maxWidth: 700, mx: "auto", textAlign: "center" }}>
              <Typography variant="body1">No questions available</Typography>
            </Box>
          ) : results ? (
            <Box
              className={`quiz-result ${mode}-mode`}
              sx={{
                padding: "40px",
                borderRadius: "12px",
                boxShadow: mode === "dark" ? "0 4px 12px rgba(243, 217, 217, 0.1)" : "0 4px 12px rgba(0, 0, 0, 0.1)",
                textAlign: "center",
                maxWidth: "800px",
                width: "100%",
                position: "relative",
                zIndex: 1,
                overflow: "hidden",
                mx: "auto",
                mt: 4,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", mb: 3, justifyContent: "center" }}>
                <TrophyIcon
                  sx={{
                    fontSize: 40,
                    color: mode === "dark" ? "#0277bd" : mode === "custom" ? customColors.text : "#0277bd",
                    mr: 1,
                  }}
                />
                <Typography
                  variant="h5"
                  component="h2"
                  sx={{
                    fontWeight: 700,
                    background: mode === "dark"
                      ? "linear-gradient(45deg, #0277bd, #01579b)"
                      : mode === "custom"
                        ? customColors.text
                        : "linear-gradient(45deg, #0277bd, #01579b)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  Quiz Results
                </Typography>
              </Box>
              {error ? (
                <Box sx={{ display: "flex", alignItems: "center", bgcolor: "#ffebee", borderRadius: "8px", p: 2, mb: 3 }}>
                  <ErrorIcon sx={{ color: "#d32f2f", mr: 1 }} />
                  <Typography sx={{ color: "#d32f2f" }}>{error}</Typography>
                </Box>
              ) : results ? (
                <Box
                  className={`quiz-result-box ${mode}-mode`}
                  sx={{
                    borderRadius: "12px",
                    p: 3,
                    boxShadow: mode === "dark" ? "0 4px 12px rgba(153, 153, 153, 0.1)" : "0 4px 12px rgba(0, 0, 0, 0.1)",
                  }}
                >
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 2, bgcolor: "#e3f2fd", borderRadius: "8px", p: 1, pr: 2 }}>
  <TrophyIcon sx={{ color: "#0277bd", mr: 1 }} />
  <Typography sx={{ fontWeight: 600, color: "#01579b" }}>
    <strong>Final Score:</strong> {results.final_score} out of {results.total_questions}
  </Typography>
</Box>
                   {/* <Box sx={{ display: "flex", alignItems: "center", mb: 2, bgcolor: "#e3f2fd", borderRadius: "8px", p: 1, pr: 2 }}>
  <InfoIcon sx={{ color: "#0277bd", mr: 1 }} />
  <Typography sx={{ fontWeight: 500, color: "#333" }}>
    <strong>Attempted Questions:</strong> {results.attempted_questions} out of {results.total_questions}
  </Typography>
</Box> */}
<Box sx={{ display: "flex", alignItems: "center", mb: 2, bgcolor: "#e3f2fd", borderRadius: "8px", p: 1, pr: 2 }}>
  <InfoIcon sx={{ color: "#0277bd", mr: 1 }} />
  <Typography sx={{ fontWeight: 500, color: "#333" }}>
    <strong>Attempts Used:</strong> {results.attempts_used} out of {maxAttempts || "Unlimited"}
  </Typography>
</Box>

                    <Box sx={{ display: "flex", alignItems: "center", mb: 2, bgcolor: "#e3f2fd", borderRadius: "8px", p: 1, pr: 2 }}>
                      <CheckIcon sx={{ color: "#0277bd", mr: 1 }} />
                      <Typography sx={{ fontWeight: 600, color: "#01579b" }}>
                        <strong>Score Percentage:</strong> {formatPercentage(results.score_percentage)}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: 2,
                        bgcolor: results.result?.toLowerCase() === "pass" ? "#e8f5e9" : "#ffebee",
                        borderRadius: "8px",
                        p: 1,
                        pr: 2,
                      }}
                    >
                      <CheckIcon
                        sx={{
                          color: results.result?.toLowerCase() === "pass" ? "#2e7d32" : "#d32f2f",
                          mr: 1,
                        }}
                      />
                      <Typography
                        sx={{
                          fontWeight: 600,
                          color: results.result?.toLowerCase() === "pass" ? "#2e7d32" : "#d32f2f",
                        }}
                      >
                        <strong>Result:</strong> {results.result}
                        {results.passing_percentage ? ` (Passing: ${formatPercentage(results.passing_percentage)})` : ""}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 2, bgcolor: "#e3f2fd", borderRadius: "8px", p: 1, pr: 2 }}>
                      <InfoIcon sx={{ color: "#0277bd", mr: 1 }} />
                      <Typography sx={{ fontWeight: 500, color: "#333" }}>
                        <strong>Attempted Questions:</strong> {results.attempted_questions} out of {results.total_questions}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ display: "flex", alignItems: "center", bgcolor: "#f0f4f8", borderRadius: "8px", p: 2, mb: 3 }}>
                  <InfoIcon sx={{ color: "#666", mr: 1 }} />
                  <Typography sx={{ color: "#666" }}>No results available yet. Try again later!</Typography>
                </Box>
              )}
              <Box sx={{ display: "flex", gap: 2, justifyContent: "center", mt: 3 }}>
                <Button
                  variant="contained"
                  sx={{
                    bgcolor: "#0277bd",
                    color: "white",
                    borderRadius: "50px",
                    px: 4,
                    py: 1.5,
                    fontWeight: 600,
                    "&:hover": { bgcolor: "#01579b", transform: "scale(1.05)", transition: "all 0.2s" },
                  }}
                  onClick={handleRetryQuiz}
                  disabled={maxAttempts && results?.attempts_used >= maxAttempts}
                >
                  Retry Quiz
                </Button>
                <Button
                  variant="outlined"
                  sx={{
                    borderColor: mode === "dark" ? "#44a6e9" : mode === "custom" ? customColors.primary : "#0277bd",
                    color: mode === "dark" ? "#44a6e9" : mode === "custom" ? customColors.text : "#0277bd",
                    borderRadius: "50px",
                    px: 4,
                    py: 1.5,
                    fontWeight: 600,
                    "&:hover": { borderColor: "#01579b", color: "#01579b", transform: "scale(1.05)", transition: "all 0.2s" },
                  }}
                  onClick={handleViewResult}
                >
                  View Detailed Results
                </Button>
              </Box>
              {/* Result modal */}
              <Modal open={isResultModalOpen} aria-labelledby="result-modal" sx={{ zIndex: 1400 }}>
                <Fade in={isResultModalOpen}>
                  <Box
                    className={`quiz-result ${mode}-mode`}
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      width: { xs: "90%", sm: 800 },
                      bgcolor: "white",
                      borderRadius: "16px",
                      boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
                      p: { xs: 3, sm: 4 },
                      maxHeight: "80vh",
                      overflowY: "auto",
                    }}
                  >
                    <IconButton
                      aria-label="close"
                      onClick={() => setIsResultModalOpen(false)}
                      sx={{
                        position: "sticky",
                        top: 2,
                        right: 2,
                        float: "right",
                        zIndex: 10001,
                        backgroundColor: "rgba(255, 255, 255, 0.69)",
                        "&:hover": {
                          backgroundColor: "rgba(255, 255, 255, 0.84)",
                        },
                      }}
                    >
                      <CloseIcon />
                    </IconButton>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 3, justifyContent: "center" }}>
                      <TrophyIcon
                        sx={{
                          fontSize: 40,
                          color: mode === "dark" ? "#44a6e9" : mode === "custom" ? customColors.text : "#0277bd",
                          mr: 1,
                        }}
                      />
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 700,
                          background: mode === "dark"
                            ? "linear-gradient(45deg, #0277bd, #01579b)"
                            : mode === "custom"
                              ? customColors.text
                              : "linear-gradient(45deg, #0277bd, #01579b)",
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent",
                        }}
                      >
                        Detailed Results
                      </Typography>
                    </Box>
                    {error && (
                      <Box
                        sx={{ display: "flex", alignItems: "center", bgcolor: "#ffebee", borderRadius: "8px", p: 2, mb: 3 }}
                      >
                        <ErrorIcon sx={{ color: "#d32f2f", mr: 1 }} />
                        <Typography sx={{ color: "#d32f2f" }}>{error}</Typography>
                      </Box>
                    )}
                    {results ? (
                      <Fade in key={animateKey} timeout={300}>
                        <Box
                          className={`quiz-result-box ${mode}-mode`}
                          sx={{
                            borderRadius: "12px",
                            p: 3,
                            boxShadow: mode === "dark" ? "0 4px 12px rgba(219, 207, 207, 0.1)" : "0 4px 12px rgba(0, 0, 0, 0.1)",
                          }}
                        >
                          <FormControl fullWidth sx={{ mb: 3 }}>
                            <Typography
                              sx={{ fontWeight: 500, color: mode === "dark" ? "#fff" : mode === "custom" ? customColors.text : "#333", mb: 1 }}
                            >
                              Select Attempt
                            </Typography>
                            <Select
                              value={selectedAttemptNo || ""}
                              onChange={handleAttemptChange}
                              MenuProps={{
                                disablePortal: true,
                                PaperProps: {
                                  sx: {
                                    zIndex: 1400,
                                    maxHeight: 200,
                                    overflow: "auto",
                                  },
                                },
                              }}
                              sx={{
                                width: "150px",
                                color: mode === "dark" ? "#000" : mode === "custom" ? customColors.text : "#000",
                                bgcolor: "#fff",
                                "& .MuiSelect-select": {
                                  py: 1,
                                },
                                "& .MuiOutlinedInput-notchedOutline": {
                                  borderColor: "#b0bec5",
                                },
                                "&:hover .MuiOutlinedInput-notchedOutline": {
                                  borderColor: "#0277bd",
                                },
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                  borderColor: "#0277bd",
                                },
                              }}
                            >
                              {attempts.map((attempt) => (
                                <MenuItem key={attempt.attempt_no} value={attempt.attempt_no}>
                                  Attempt {attempt.attempt_no}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
                            {paginatedFields.map((field, index) => (
                              <Box
                                key={index}
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  mb: 2,
                                  bgcolor: field.bgcolor || "#e3f2fd",
                                  borderRadius: "8px",
                                  p: 1,
                                  pr: 2,
                                }}
                              >
                                <field.icon sx={{ color: field.color || "#0277bd", mr: 1 }} />
                                <Typography
                                  sx={{ fontWeight: field.label === "Result" || field.label === "Final Score" || field.label === "Score Percentage" ? 600 : 500, color: field.color || "#333" }}
                                >
                                  <strong>{field.label}:</strong> {field.value}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                          <Pagination
                            count={totalPages}
                            page={currentPage}
                            onChange={(event, value) => setCurrentPage(value)}
                            sx={{ mt: 3, display: "flex", justifyContent: "center" }}
                          />
                        </Box>
                      </Fade>
                    ) : (
                      <Box sx={{ display: "flex", alignItems: "center", bgcolor: "#f0f4f8", borderRadius: "8px", p: 2, mb: 3 }}>
                        <InfoIcon sx={{ color: "#666", mr: 1 }} />
                        <Typography sx={{ color: "#666" }}>No results available.</Typography>
                      </Box>
                    )}
                    <Box sx={{ display: "flex", gap: 2, justifyContent: "center", mt: 3 }}>
                      <Button
                        variant="contained"
                        sx={{
                          bgcolor: "#388E3C",
                          color: "white",
                          borderRadius: "50px",
                          px: 4,
                          py: 1.5,
                          fontWeight: 600,
                          "&:hover": { bgcolor: "#2E7D32", transform: "scale(1.05)", transition: "all 0.2s" },
                        }}
                        onClick={fetchReviewCorrectAnswers}
                      >
                        Review Correct Answer
                      </Button>
                    </Box>
                  </Box>
                </Fade>
              </Modal>

              {/* Review Correct Answers modal */}
              <Modal
                open={isReviewCorrectModalOpen}
                aria-labelledby="review-correct-modal-title"
                aria-describedby="review-correct-modal-description"
                sx={{
                  backdropFilter: "blur(4px)",
                  zIndex: 1500, // Increase the zIndex to ensure it appears above other modals
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: { xs: "90%", sm: 650 },
                    maxHeight: "85vh",
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: "20px",
                    boxShadow: mode === "dark"
                      ? "0 20px 60px rgba(0, 0, 0, 0.5)"
                      : "0 20px 60px rgba(30, 58, 138, 0.15)",
                    border: mode === "dark"
                      ? "1px solid rgba(255, 255, 255, 0.1)"
                      : "1px solid rgba(30, 139, 175, 0.1)",
                    background: mode === "dark"
                      ? "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)"
                      : mode === "custom"
                        ? `linear-gradient(135deg, ${customColors?.background || "#f5f5f5"} 0%, ${customColors?.primary || "#1e8bafff"}15 100%)`
                        : "linear-gradient(135deg, #ffffff 0%, #f0f7ff 100%)",
                    overflow: "hidden",
                  }}
                >
                  {/* Sticky Header */}
                  <Box
                    sx={{
                      position: "sticky",
                      top: 0,
                      zIndex: 2,
                      background: mode === "dark"
                        ? "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)"
                        : mode === "custom"
                          ? `linear-gradient(135deg, ${customColors?.background || "#f5f5f5"} 0%, ${customColors?.primary || "#1e8bafff"}15 100%)`
                          : "linear-gradient(135deg, #ffffff 0%, #f0f7ff 100%)",
                      p: 4,
                      pb: 2,
                      borderRadius: "20px 20px 0 0",
                      boxShadow: mode === "dark"
                        ? "0 4px 12px rgba(0, 0, 0, 0.3)"
                        : "0 4px 12px rgba(30, 139, 175, 0.1)",
                    }}
                  >
               

                    {/* Modal Heading */}
                    <Typography
                      id="review-correct-modal-title"
                      variant="h5"
                      sx={{
                        background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        backgroundClip: "text",
                        mb: 1,
                        fontWeight: 700,
                        letterSpacing: "-0.5px",
                      }}
                    >
                      Review Correct Answers (Attempt {attemptNumber || "N/A"})
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: mode === "dark"
                          ? "rgba(255, 255, 255, 0.6)"
                          : mode === "custom"
                            ? customColors?.text
                              ? `${customColors.text}99`
                              : "rgba(0, 0, 0, 0.6)"
                            : "rgba(0, 0, 0, 0.6)",
                        fontSize: "0.875rem",
                      }}
                    >
                      Review the correct answers for your quiz attempt
                    </Typography>
                  </Box>

                  {/* Scrollable Content */}
                  <Box
                    sx={{
                      flex: 1,
                      overflowY: "auto",
                      p: 2,
                      // Scrollbar styling
                      "&::-webkit-scrollbar": {
                        width: "6px",
                      },
                      "&::-webkit-scrollbar-track": {
                        background: "transparent",
                      },
                      "&::-webkit-scrollbar-thumb": {
                        background: mode === "dark"
                          ? "rgba(255, 255, 255, 0.3)"
                          : "rgba(30, 139, 175, 0.3)",
                        borderRadius: "4px",
                      },
                    }}
                  >
                    {reviewCorrectData.length > 0 ? (
                      reviewCorrectData.map((item, index) => renderReviewCorrectAnswer(item, index))
                    ) : (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: mode === "dark" ? "rgba(0, 0, 0, 0.3)" : "rgba(30, 139, 175, 0.1)",
                          borderRadius: "8px",
                          p: 2,
                          mb: 3,
                        }}
                      >
                        <InfoIcon sx={{ color: mode === "dark" ? "#ffffff" : "#1e3a8a", mr: 1 }} />
                        <Typography sx={{ color: mode === "dark" ? "#ffffff" : "#1e3a8a" }}>
                          No review data available.
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {/* Sticky Footer */}
                  <Box
                    sx={{
                      position: "sticky",
                      bottom: 0,
                      zIndex: 2,
                      background: mode === "dark"
                        ? "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)"
                        : mode === "custom"
                          ? `linear-gradient(135deg, ${customColors?.background || "#f5f5f5"} 0%, ${customColors?.primary || "#1e8bafff"}15 100%)`
                          : "linear-gradient(135deg, #ffffff 0%, #f0f7ff 100%)",
                      p: 2,
                      borderRadius: "0 0 20px 20px",
                      boxShadow: mode === "dark"
                        ? "0 -4px 12px rgba(0, 0, 0, 0.3)"
                        : "0 -4px 12px rgba(30, 139, 175, 0.1)",
                      borderTop: mode === "dark" ? "1px solid rgba(255, 255, 255, 0.1)" : "1px solid rgba(30, 139, 175, 0.1)",
                    }}
                  >
                    <Button
                      variant="contained"
                      onClick={() => setIsReviewCorrectModalOpen(false)}
                      sx={{
                        float: "right",
                        background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
                        color: "white",
                        fontWeight: 600,
                        fontSize: "0.95rem",
                        textTransform: "none",
                        px: 3,
                        py: 1.25,
                        borderRadius: "12px",
                        boxShadow: "0 4px 14px rgba(30, 139, 175, 0.3)",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          background: "linear-gradient(135deg, #1e3a8a 0%, #1a7a9a 100%)",
                          boxShadow: "0 6px 20px rgba(30, 139, 175, 0.4)",
                          transform: "translateY(-2px)",
                        },
                        "&:active": {
                          transform: "translateY(0px)",
                          boxShadow: "0 2px 8px rgba(30, 139, 175, 0.3)",
                        },
                      }}
                    >
                      Close
                    </Button>
                  </Box>
                </Box>
              </Modal>

            </Box>
          ) : currentQuestionIndex >= questions.length ? (
            <Box sx={{ maxWidth: 700, mx: "auto", textAlign: "center" }}>
              <Typography variant="body1">Practice complete! All questions have been displayed.</Typography>
            </Box>
          ) : (
            <Box>
              {/* <Typography
                variant="h4"
                gutterBottom
                align="center"
                sx={{ color: mode === "dark" ? "#e9d6d6ff" : mode === "custom" ? customColors.text || "#142be1" : "#142be1", fontWeight: "bold", mb: 4 }}
              >
                Practice Questions
              </Typography> */}
              {renderQuestion(questions[currentQuestionIndex], currentQuestionIndex)}
              {/* Question list modal */}
              <Modal
                open={questionListOpen}
                aria-labelledby="question-list-modal"
                sx={{ zIndex: 10001 }}
              >
                <Fade in={questionListOpen}>
                  <Box
                    sx={{
                      position: "fixed",
                      top: 64,
                      right: 80,
                      width: { xs: "80%", sm: 300 },
                      bgcolor: "white",
                      borderRadius: "12px",
                      boxShadow: "0 4px 16px rgba(0, 0, 0, 0.2)",
                      p: { xs: 2, sm: 3 },
                      background: mode === "dark"
                        ? "linear-gradient(145deg, #212121, #424242)"
                        : mode === "custom"
                          ? customColors.background
                          : "linear-gradient(145deg, #ffffff, #f0f4f8)",
                      maxHeight: "60vh",
                      overflowY: "auto",
                    }}
                  >
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: "flex", gap: 0.8, mb: 1, flexWrap: "nowrap" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <Box
                            sx={{
                              width: 16,
                              height: 16,
                              bgcolor: mode === "dark" ? "#4a1c1c" : mode === "custom" ? customColors.error || "#f8bbc4" : "#f8bbc4",
                              borderRadius: "4px",
                            }}
                          />
                          <Typography sx={{ fontSize: "0.7rem", color: mode === "dark" ? "#E0E0E0" : "#333" }}>
                            Skipped
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <Box
                            sx={{
                              width: 16,
                              height: 16,
                              bgcolor: mode === "dark" ? "#1b5e20" : mode === "custom" ? customColors.success || "#aefab4" : "#aefab4",
                              borderRadius: "4px",
                            }}
                          />
                          <Typography sx={{ fontSize: "0.7rem", color: mode === "dark" ? "#E0E0E0" : "#333" }}>
                            Attempted
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <Box
                            sx={{
                              width: 16,
                              height: 16,
                              bgcolor: mode === "dark" ? "#2c2c2c" : "#ffffff",
                              border: "1px solid #b0bec5",
                              borderRadius: "4px",
                            }}
                          />
                          <Typography sx={{ fontSize: "0.7rem", color: mode === "dark" ? "#E0E0E0" : "#333" }}>
                            Not Attempted
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Typography
                          id="question-list-modal"
                          variant="h6"
                          sx={{
                            fontWeight: 600,
                            color: mode === "custom" ? customColors.text : "#0277bd",
                          }}
                        >
                          Question List
                        </Typography>
                        <IconButton
                          aria-label="close"
                          onClick={() => setQuestionListOpen(false)}
                          sx={{
                            backgroundColor: "rgba(255, 255, 255, 0.69)",
                            "&:hover": {
                              backgroundColor: "rgba(255, 255, 255, 0.84)",
                            },
                          }}
                        >
                          <CloseIcon sx={{ fontSize: "1.2rem" }} />
                        </IconButton>
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fill, minmax(40px, 1fr))",
                        gap: 1,
                        justifyItems: "center",
                        mb: 2,
                      }}
                    >
                      {questions.map((_, index) => {
                        const isAttempted = questionStatuses[index] === "attempted";
                        const isSkipped = questionStatuses[index] === "skipped";
                        return (
                          <Box
                            key={index}
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: "50%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              bgcolor:
                                mode === "dark"
                                  ? isAttempted
                                    ? "#1b5e20"
                                    : isSkipped
                                      ? "#4a1c1c"
                                      : "#2c2c2c"
                                  : mode === "custom"
                                    ? isAttempted
                                      ? customColors.success || "#aefab4"
                                      : isSkipped
                                        ? customColors.error || "#f8bbc4"
                                        : "#ffffff"
                                    : isAttempted
                                      ? "#aefab4"
                                      : isSkipped
                                        ? "#f8bbc4"
                                        : "transparent",
                              border: isAttempted || isSkipped ? "none" : "1px solid #b0bec5",
                              cursor: "pointer",
                              fontWeight: 500,
                              color:
                                mode === "dark"
                                  ? isAttempted
                                    ? "#65e76b"
                                    : isSkipped
                                      ? "#fc6767"
                                      : "#E0E0E0"
                                  : mode === "custom"
                                    ? "#000000"
                                    : isAttempted
                                      ? "#2e7d32"
                                      : isSkipped
                                        ? "#d32f2f"
                                        : "#333",
                              "&:hover": {
                                bgcolor:
                                  mode === "dark"
                                    ? isAttempted
                                      ? "#376e3a"
                                      : isSkipped
                                        ? "#7F1D1D"
                                        : "#333"
                                    : mode === "custom"
                                      ? isAttempted
                                        ? customColors.successHover || "#d0eddb"
                                        : isSkipped
                                          ? customColors.errorHover || "#ffccd5"
                                          : customColors.hover || "#f0f4f8"
                                      : isAttempted
                                        ? "#d0eddb"
                                        : isSkipped
                                          ? "#ffccd5"
                                          : "#f0f4f8",
                              },
                            }}
                            onClick={() => {
                              setCurrentQuestionIndex(index);
                              setQuestionListOpen(false);
                            }}
                          >
                            {index + 1}
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                </Fade>
              </Modal>
              {/* Review modal */}
              <Modal
  open={reviewModalOpen}
  aria-labelledby="review-modal"
  sx={{
    backdropFilter: "blur(4px)",
    zIndex: 10001,
  }}
>
  <Fade in={reviewModalOpen}>
    <Box
      sx={{
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: { xs: "90%", sm: 600 },
        maxHeight: "85vh",
        display: "flex",
        flexDirection: "column",
        borderRadius: "20px",
        boxShadow: mode === "dark"
          ? "0 20px 60px rgba(0, 0, 0, 0.5)"
          : "0 20px 60px rgba(30, 58, 138, 0.15)",
        border: mode === "dark"
          ? "1px solid rgba(255, 255, 255, 0.1)"
          : "1px solid rgba(30, 139, 175, 0.1)",
        background: mode === "dark"
          ? "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)"
          : mode === "custom"
          ? `linear-gradient(135deg, ${customColors?.background || "#f5f5f5"} 0%, ${customColors?.primary || "#1e8bafff"}15 100%)`
          : "linear-gradient(135deg, #ffffff 0%, #f0f7ff 100%)",
        overflow: "hidden",
      }}
    >
      {/* Sticky Header */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 2,
          background: mode === "dark"
            ? "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)"
            : mode === "custom"
            ? `linear-gradient(135deg, ${customColors?.background || "#f5f5f5"} 0%, ${customColors?.primary || "#1e8bafff"}15 100%)`
            : "linear-gradient(135deg, #ffffff 0%, #f0f7ff 100%)",
          p: 3,
          pb: 2,
          borderRadius: "20px 20px 0 0",
          boxShadow: mode === "dark"
            ? "0 4px 12px rgba(0, 0, 0, 0.3)"
            : "0 4px 12px rgba(30, 139, 175, 0.1)",
        }}
      >
        {/* Modal Heading */}
        <Typography
          id="review-modal"
          variant="h5"
          sx={{
            background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
            mb: 1,
            fontWeight: 700,
            letterSpacing: "-0.5px",
            textAlign: "center",
          }}
        >
          Review Your Answers
        </Typography>
      </Box>

      {/* Scrollable Content */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          p: 2,
          // Scrollbar styling
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            background: mode === "dark"
              ? "rgba(255, 255, 255, 0.3)"
              : "rgba(30, 139, 175, 0.3)",
            borderRadius: "4px",
          },
        }}
      >
        {questions.map((question, index) => (
          <Box
            key={index}
            sx={{
              p: 2,
              mb: 2,
              borderRadius: "12px",
              backgroundColor: mode === "dark"
                ? "rgba(255, 255, 255, 0.05)"
                : mode === "custom"
                ? customColors?.inputBg || "rgba(255, 255, 255, 0.8)"
                : "rgba(255, 255, 255, 0.8)",
              border: `1.5px solid ${
                mode === "dark"
                  ? "rgba(255, 255, 255, 0.1)"
                  : "rgba(30, 139, 175, 0.15)"
              }`,
              transition: "all 0.3s ease",
              "&:hover": {
                backgroundColor: mode === "dark"
                  ? "rgba(255, 255, 255, 0.08)"
                  : "rgba(255, 255, 255, 1)",
                borderColor: "rgba(30, 139, 175, 0.4)",
                transform: "translateY(-2px)",
                boxShadow: mode === "dark"
                  ? "0 4px 12px rgba(0, 0, 0, 0.3)"
                  : "0 4px 12px rgba(30, 139, 175, 0.1)",
              },
            }}
          >
            {renderReviewQuestion(question, index)}
          </Box>
        ))}
      </Box>

      {/* Sticky Footer */}
      <Box
        sx={{
          position: "sticky",
          bottom: 0,
          zIndex: 2,
          background: mode === "dark"
            ? "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)"
            : mode === "custom"
            ? `linear-gradient(135deg, ${customColors?.background || "#f5f5f5"} 0%, ${customColors?.primary || "#1e8bafff"}15 100%)`
            : "linear-gradient(135deg, #ffffff 0%, #f0f7ff 100%)",
          p: 2,
          borderRadius: "0 0 20px 20px",
          boxShadow: mode === "dark"
            ? "0 -4px 12px rgba(0, 0, 0, 0.3)"
            : "0 -4px 12px rgba(30, 139, 175, 0.1)",
          borderTop: mode === "dark" ? "1px solid rgba(255, 255, 255, 0.1)" : "1px solid rgba(30, 139, 175, 0.1)",
        }}
      >
        <Button
          variant="contained"
          onClick={handleReviewClose}
          sx={{
            float: "right",
            background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
            color: "white",
            fontWeight: 600,
            fontSize: "0.95rem",
            textTransform: "none",
            px: 3,
            py: 1.25,
            borderRadius: "12px",
            boxShadow: "0 4px 14px rgba(30, 139, 175, 0.3)",
            transition: "all 0.3s ease",
            "&:hover": {
              background: "linear-gradient(135deg, #1e3a8a 0%, #1a7a9a 100%)",
              boxShadow: "0 6px 20px rgba(30, 139, 175, 0.4)",
              transform: "translateY(-2px)",
            },
            "&:active": {
              transform: "translateY(0px)",
              boxShadow: "0 2px 8px rgba(30, 139, 175, 0.3)",
            },
          }}
        >
          Close
        </Button>
      </Box>
    </Box>
  </Fade>
</Modal>

              {/* Confirmation modal for canceling quiz during practice */}
              <Dialog
                open={isConfirmModalOpen}
                onClose={handleCancelConfirmModal}
                maxWidth="sm"
                fullWidth
                sx={{
                  "& .MuiDialog-paper": {
                    borderRadius: "16px",
                    p: 3,
                    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
                    width: "450px",
                    maxWidth: "90%",
                    backgroundColor: mode === "dark" ? "#424242" : mode === "custom" ? customColors.background || "#ffffff" : "#ffffff",
                  },
                }}
              >
                <DialogContent sx={{ p: 3 }}>
                  <Fade in={isConfirmModalOpen} timeout={300}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <ErrorIcon
                        sx={{ color: mode === "dark" ? "#0277BD" : mode === "custom" ? customColors.text || "#0277BD" : "#0277BD", fontSize: 32 }}
                      />
                      <Typography
                        variant="h6"
                        sx={{
                          color: mode === "dark" ? "#f1f1f1" : mode === "custom" ? customColors.text || "#1A1A1A" : "#1A1A1A",
                          fontWeight: 500,
                          lineHeight: 1.4,
                        }}
                      >
                        Are you sure you want to close the quiz? Your progress will be lost.
                      </Typography>
                    </Box>
                  </Fade>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3, justifyContent: "center", gap: 1 }}>
                  <Button
                    variant="contained"
                    onClick={handleCancelConfirmModal}
                    sx={{
                      color: "#0277BD",
                      borderColor: "#0277BD",
                      borderRadius: "50px",
                      bgcolor: "#b1d1ebff",
                      px: 4,
                      py: 1.5,
                      fontSize: "1rem",
                      fontWeight: 600,
                      "&:hover": {
                        bgcolor: "#E1F5FE",
                        borderColor: "#01579B",
                        transform: "scale(1.05)",
                        transition: "all 0.2s",
                      },
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleConfirmCancel}
                    sx={{
                      bgcolor: "#0277BD",
                      color: "#fff",
                      borderRadius: "50px",
                      px: 4,
                      py: 1.5,
                      fontSize: "1rem",
                      fontWeight: 600,
                      "&:hover": {
                        bgcolor: "#01579B",
                        transform: "scale(1.05)",
                        transition: "all 0.2s",
                      },
                    }}
                  >
                    Confirm
                  </Button>
                </DialogActions>
              </Dialog>
              {/* Offline dialog */}
              <Modal
                open={isOfflineModalOpen}
                onClose={() => setIsOfflineModalOpen(false)}
                aria-labelledby="no-internet-modal"
                sx={{ zIndex: 9999 }}
              >
                <Fade in={isOfflineModalOpen}>
                  <Box
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      width: { xs: "90%", sm: 400 },
                      bgcolor: "white",
                      borderRadius: "16px",
                      boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
                      p: { xs: 3, sm: 4 },
                      background: "linear-gradient(145deg, #ffffff, #f0f4f8)",
                      animation: "scaleIn 0.3s ease-out",
                      "@keyframes scaleIn": {
                        "0%": {
                          transform: "translate(-50%, -50%) scale(0.8)",
                          opacity: 0,
                        },
                        "100%": {
                          transform: "translate(-50%, -50%) scale(1)",
                          opacity: 1,
                        },
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: 3,
                        justifyContent: "center",
                      }}
                    >
                      <ErrorIcon sx={{ fontSize: 30, color: "#d32f2f", mr: 1 }} />
                      <Typography
                        id="no-internet-modal"
                        variant="h5"
                        component="h2"
                        sx={{
                          fontWeight: 650,
                          background: "linear-gradient(45deg, #d32f2f, #b71c1c)",
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent",
                        }}
                      >
                        No Internet Connection
                      </Typography>
                    </Box>
                    <Typography sx={{ mb: 3, color: "#333", textAlign: "center" }}>
                      No internet connection. Your answers have been saved and will be submitted when you're back online.
                    </Typography>
                  </Box>
                </Fade>
              </Modal>
              {/* Timeout modal */}
              <Modal
                open={isTimeoutModalOpen}
                aria-labelledby="timeout-modal"
                sx={{ zIndex: 9999 }}
              >
                <Fade in={isTimeoutModalOpen}>
                  <Box
                    className={`quiz-modal ${mode}-mode`}
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      width: { xs: "90%", sm: 400 },
                      bgcolor: "white",
                      borderRadius: "16px",
                      boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
                      p: { xs: 3, sm: 4 },
                      animation: "scaleIn 0.3s ease-out",
                      "@keyframes scaleIn": {
                        "0%": {
                          transform: "translate(-50%, -50%) scale(0.8)",
                          opacity: 0,
                        },
                        "100%": {
                          transform: "translate(-50%, -50%) scale(1)",
                          opacity: 1,
                        },
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: 3,
                        justifyContent: "center",
                      }}
                    >
                      <TimeIcon sx={{ fontSize: 30, color: "#d32f2f", mr: 1 }} />
                      <Typography
                        id="timeout-modal"
                        variant="h5"
                        component="h2"
                        sx={{
                          fontWeight: 650,
                          background: "linear-gradient(45deg, #d32f2f, #b71c1c)",
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent",
                        }}
                      >
                        Time's Up!
                      </Typography>
                    </Box>
                    <Typography
                      sx={{ mb: 3, color: mode === "dark" ? "white" : mode === "custom" ? customColors.text : "#333", textAlign: "center" }}
                    >
                      The time for the quiz has expired. Click below to view your results.
                    </Typography>
                    <Box sx={{ display: "flex", justifyContent: "center" }}>
                      <Button
                        variant="contained"
                        onClick={() => {
                          setIsTimeoutModalOpen(false);
                          handleSubmitQuiz();
                        }}
                        sx={{
                          bgcolor: "#0277bd",
                          color: "white",
                          borderRadius: "50px",
                          px: 4,
                          py: 1.5,
                          fontWeight: 600,
                          "&:hover": { bgcolor: "#01579b", transform: "scale(1.05)", transition: "all 0.2s" },
                        }}
                      >
                        View Result
                      </Button>
                    </Box>
                  </Box>
                </Fade>
              </Modal>
              {/* Unanswered questions dialog */}
              <Dialog
  open={openConfirmDialog}
  maxWidth="sm"
  fullWidth
  sx={{
    "& .MuiDialog-paper": {
      borderRadius: "20px",
      boxShadow: mode === "dark"
        ? "0 20px 60px rgba(0, 0, 0, 0.5)"
        : "0 20px 60px rgba(30, 58, 138, 0.15)",
      border: mode === "dark"
        ? "1px solid rgba(255, 255, 255, 0.1)"
        : "1px solid rgba(30, 139, 175, 0.1)",
      background: mode === "dark"
        ? "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)"
        : mode === "custom"
        ? `linear-gradient(135deg, ${customColors?.background || "#f5f5f5"} 0%, ${customColors?.primary || "#1e8bafff"}15 100%)`
        : "linear-gradient(135deg, #ffffff 0%, #f0f7ff 100%)",
    },
  }}
>
  {/* Dialog Title */}
  <DialogTitle
    sx={{
      position: "relative",
      background: mode === "dark"
        ? "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)"
        : mode === "custom"
        ? `linear-gradient(135deg, ${customColors?.background || "#f5f5f5"} 0%, ${customColors?.primary || "#1e8bafff"}15 100%)`
        : "linear-gradient(135deg, #ffffff 0%, #f0f7ff 100%)",
      color: mode === "dark"
        ? "#ffffff"
        : mode === "custom"
        ? customColors?.text || "#1e3a8a"
        : "#1e3a8a",
      fontWeight: 700,
      textAlign: "start",
      p: 3,
      pb: 1,
    }}
  >
    Unanswered Questions
    {/* Close Icon */}
    <IconButton
      aria-label="close"
      onClick={() => setOpenConfirmDialog(false)}
      sx={{
        position: "absolute",
        top: 16,
        right: 16,
        color: mode === "dark" ? "#ffffff" : "#1e3a8a",
        backgroundColor: mode === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(30, 139, 175, 0.1)",
        "&:hover": {
          backgroundColor: mode === "dark" ? "rgba(255, 255, 255, 0.2)" : "rgba(30, 139, 175, 0.2)",
        },
      }}
    >
      <CloseIcon fontSize="small" />
    </IconButton>
  </DialogTitle>

  {/* Dialog Content */}
  <DialogContent
    sx={{
      background: mode === "dark"
        ? "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)"
        : mode === "custom"
        ? `linear-gradient(135deg, ${customColors?.background || "#f5f5f5"} 0%, ${customColors?.primary || "#1e8bafff"}15 100%)`
        : "linear-gradient(135deg, #ffffff 0%, #f0f7ff 100%)",
      color: mode === "dark"
        ? "#ffffff"
        : mode === "custom"
        ? customColors?.text || "#111111"
        : "#111111",
      p: 2,
      pt: 0,
    }}
  >
    <DialogContentText
      sx={{
        color: mode === "dark"
          ? "rgba(255, 255, 255, 0.8)"
          : mode === "custom"
          ? `${customColors?.text}99`
          : "rgba(0, 0, 0, 0.6)",
        mb: 2,
        fontSize: "0.95rem",
      }}
    >
      You still have {remainingQuestions.length} unanswered questions:
    </DialogContentText>

    {/* Scrollable List */}
    <Box
      sx={{
        maxHeight: "200px",
        overflowY: "auto",
        mt: 1,
        p: 1,
        border: `1px solid ${
          mode === "dark"
            ? "rgba(255, 255, 255, 0.1)"
            : "rgba(30, 139, 175, 0.15)"
        }`,
        borderRadius: "8px",
        backgroundColor: mode === "dark"
          ? "rgba(255, 255, 255, 0.05)"
          : mode === "custom"
          ? customColors?.inputBg || "rgba(255, 255, 255, 0.6)"
          : "rgba(255, 255, 255, 0.6)",
        // Custom scrollbar
        "&::-webkit-scrollbar": {
          width: "6px",
        },
        "&::-webkit-scrollbar-track": {
          background: "transparent",
        },
        "&::-webkit-scrollbar-thumb": {
          background: mode === "dark"
            ? "rgba(255, 255, 255, 0.3)"
            : "rgba(30, 139, 175, 0.3)",
          borderRadius: "4px",
        },
      }}
    >
      <ul style={{ margin: 0, padding: "8px 16px" }}>
        {remainingQuestions.map((q, index) => (
          <li
            key={index}
            style={{
              marginBottom: "8px",
              padding: "8px",
              borderRadius: "6px",
              backgroundColor: mode === "dark"
                ? "rgba(255, 255, 255, 0.08)"
                : mode === "custom"
                ? customColors?.inputBg || "rgba(230, 230, 230, 0.4)"
                : "rgba(230, 230, 230, 0.4)",
              color: mode === "dark"
                ? "#ffffff"
                : mode === "custom"
                ? customColors?.text || "#111111"
                : "#111111",
              fontWeight: 500,
              listStyle: "none",
            }}
          >
            <span style={{ fontWeight: 600 }}>
              {q.question_type ? `[${q.question_type.toUpperCase()}] ` : ""}
            </span>
            {index + 1}. {renderContent(formatQuestionText(q))}
          </li>
        ))}
      </ul>
    </Box>

    <DialogContentText
      sx={{
        color: mode === "dark"
          ? "rgba(255, 255, 255, 0.8)"
          : mode === "custom"
          ? `${customColors?.text}99`
          : "rgba(0, 0, 0, 0.6)",
        mt: 2,
        fontSize: "0.95rem",
      }}
    >
      Do you want to submit anyway?
    </DialogContentText>
  </DialogContent>

  {/* Dialog Actions */}
  <DialogActions
    sx={{
      background: mode === "dark"
        ? "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)"
        : mode === "custom"
        ? `linear-gradient(135deg, ${customColors?.background || "#f5f5f5"} 0%, ${customColors?.primary || "#1e8bafff"}15 100%)`
        : "linear-gradient(135deg, #ffffff 0%, #f0f7ff 100%)",
      p: 2,
      borderTop: mode === "dark"
        ? "1px solid rgba(255, 255, 255, 0.1)"
        : "1px solid rgba(30, 139, 175, 0.1)",
    }}
  >
    <Button
      onClick={() => setOpenConfirmDialog(false)}
      variant="outlined"
      sx={{
        color: mode === "dark"
          ? "#44a6e9"
          : mode === "custom"
          ? customColors?.text || "#0277bd"
          : "#0277bd",
        borderColor: mode === "dark"
          ? "#44a6e9"
          : mode === "custom"
          ? customColors?.text || "#0277bd"
          : "#0277bd",
        "&:hover": {
          borderColor: mode === "dark"
            ? "#44a6e9"
            : mode === "custom"
            ? customColors?.text || "#0277bd"
            : "#0277bd",
          backgroundColor: mode === "dark"
            ? "rgba(68, 166, 233, 0.1)"
            : mode === "custom"
            ? "rgba(2, 119, 189, 0.1)"
            : "rgba(2, 119, 189, 0.1)",
        },
      }}
    >
      No, Go Back
    </Button>
    <Button
      onClick={submitQuiz}
      color="primary"
      variant="contained"
      disabled={isSubmitting}
      sx={{
        background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
        color: "white",
        fontWeight: 600,
        fontSize: "0.95rem",
        textTransform: "none",
        px: 3,
        py: 1,
        borderRadius: "12px",
        boxShadow: "0 4px 14px rgba(30, 139, 175, 0.3)",
        transition: "all 0.3s ease",
        "&:hover": {
          background: "linear-gradient(135deg, #1e3a8a 0%, #1a7a9a 100%)",
          boxShadow: "0 6px 20px rgba(30, 139, 175, 0.4)",
          transform: "translateY(-2px)",
        },
        "&:active": {
          transform: "translateY(0px)",
          boxShadow: "0 2px 8px rgba(30, 139, 175, 0.3)",
        },
      }}
    >
      {isSubmitting ? "Submitting..." : "Yes, Submit"}
    </Button>
  </DialogActions>
</Dialog>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default PracticePage;